import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { classMap } from 'lit/directives/class-map.js';
import { currentLang, setLang, t, type Lang } from '../core/i18n';
import {
  store,
  save,
  load,
  hasSave,
  framesEarned,
  frameMultiplier,
  effectiveMarketDemand,
  axiomPointsEarned,
  hasAxiom,
  reactorDurationMultiplier,
  userRecipePhiCost,
  USER_RECIPE_MAX,
  USER_RECIPE_VALUE_RATIO,
} from '../core/store';
import { AXIOMS } from '../content/axioms';
import { NARRATOR_BEATS as NARRATOR_BEATS_REF } from '../content/narrator';
import {
  setAct as audioSetAct,
  bellSound,
  clickSound,
  initAudio,
  isMuted,
  setMuted,
  getVolume,
  setVolume,
} from '../core/audio';
import { startTick, stopTick } from '../core/tick';
import { ENCOUNTERS } from '../content/encounters';
import { RECIPES } from '../content/recipes';
import { ITEMS } from '../content/items';
import { STEWARDS, stewardCost } from '../content/stewards';
import { REACTORS, reactorCost } from '../content/reactors';
import { fmt, fmtInt, fmtPhi } from '../core/numbers';
import type {
  ContradictionClass,
  LedgerEntry,
  ResourceId,
  UserRecipe,
} from '../types';
import { Decimal } from '../core/numbers';

const RES_DISPLAY: ResourceId[] = [
  'coin',
  'xp',
  'wood',
  'ore',
  'hide',
  'herb',
  'ether',
];

const RESOURCE_ICON: Partial<Record<ResourceId, string>> = {
  coin: '◉',
  xp: '✦',
  wood: '⌬',
  ore: '◆',
  hide: '⬢',
  herb: '✿',
  ether: '✶',
  plank: '▤',
  ingot: '▰',
  salve: '◌',
  charm: '⚭',
  shortSword: '†',
  cart: '⛓',
  reinforcedCharm: '⚮',
  elixir: '◍',
  anvilPiece: '▪',
  crystal: '✦',
  silk: '~',
  bone: '⚐',
  lantern: '☼',
  tome: '▦',
  reliquary: '✟',
  standard: '⚑',
  swordOfFirstCut: '✠',
};

const TAB_VALUES = [
  'encounters',
  'stewards',
  'reactors',
  'inventory',
  'crafting',
  'market',
] as const;
type Tab = (typeof TAB_VALUES)[number];

function formatTime(t: number): string {
  const d = new Date(t);
  return (
    d.getHours().toString().padStart(2, '0') +
    ':' +
    d.getMinutes().toString().padStart(2, '0') +
    ':' +
    d.getSeconds().toString().padStart(2, '0')
  );
}

@customElement('tpe-game')
export class TpeGame extends LitElement {
  protected createRenderRoot(): HTMLElement {
    return this;
  }

  @state() private langCode: Lang = currentLang();
  @state() private tab: Tab = 'encounters';
  @state() private tick = 0;
  @state() private ledgerOpen = false;
  @state() private realignOpen = false;
  @state() private editorOpen = false;
  @state() private editorInputs: { id: ResourceId; amount: number }[] = [
    { id: 'wood', amount: 1 },
  ];
  @state() private editorOutput: { id: ResourceId; amount: number } = {
    id: 'plank',
    amount: 1,
  };
  @state() private editorError = '';
  @state() private axiomsOpen = false;
  @state() private recompileOpen = false;
  @state() private equilibriumOpen = false;
  @state() private creditsOpen = false;
  @state() private statsOpen = false;
  @state() private beatsOpen = false;
  @state() private complianceScrollActive = false;
  @state() private muted = isMuted();
  private lastBeatQueueLen = 0;
  private lastAct: number | string = 1;
  private lastEquilibriumAt = 0;

  private unsubStore?: () => void;
  private rafId = 0;

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('tpe:lang-change', this.onLang);
    let lastRestoredFantasy = store.getState().meta.restoredFantasy;
    this.unsubStore = store.subscribe(() => {
      this.tick++;
      const st = store.getState();
      const a = st.act;
      if (a !== this.lastAct) {
        this.lastAct = a;
        this.syncBodyTheme(a);
        audioSetAct(a as 1 | 2 | 3 | 4 | 'endgame');
        if (a === 3) {
          store.getState().openMarket();
          if (this.tab === 'inventory') this.tab = 'market';
        }
      }
      // Ring a bell whenever a new narrator beat is queued.
      if (st.narratorQueue.length > this.lastBeatQueueLen) bellSound();
      this.lastBeatQueueLen = st.narratorQueue.length;
      if (st.meta.restoredFantasy !== lastRestoredFantasy) {
        lastRestoredFantasy = st.meta.restoredFantasy;
        this.syncBodyTheme(a);
      }
      // Auto-open Equilibrium modal the moment it first surfaces.
      if (
        st.meta.equilibriumAt > 0 &&
        st.meta.equilibriumAt !== this.lastEquilibriumAt &&
        !st.meta.committed
      ) {
        this.lastEquilibriumAt = st.meta.equilibriumAt;
        this.equilibriumOpen = true;
      }
      this.requestUpdate();
    });
    const initialAct = store.getState().act;
    this.syncBodyTheme(initialAct);
    this.lastAct = initialAct;
    audioSetAct(initialAct as 1 | 2 | 3 | 4 | 'endgame');
    if (hasSave()) load();
    startTick();
    this.uiLoop();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('tpe:lang-change', this.onLang);
    this.unsubStore?.();
    stopTick();
    cancelAnimationFrame(this.rafId);
  }

  private onLang = (e: Event): void => {
    this.langCode = (e as CustomEvent<Lang>).detail;
    this.requestUpdate();
  };

  /** Light UI loop just to redraw progress bars smoothly between state ticks. */
  private uiLoop = (): void => {
    this.tick++;
    this.requestUpdate();
    this.rafId = requestAnimationFrame(this.uiLoop);
  };

  // ── helpers ───────────────────────────────────────────────────────────
  private name(o: { en: string; pt: string }): string {
    return this.langCode === 'pt-br' ? o.pt : o.en;
  }
  private resourceName(id: ResourceId): string {
    return this.name(ITEMS[id].name);
  }
  private syncBodyTheme(act: number | string): void {
    document.body.classList.remove(
      'theme-act-1',
      'theme-act-2',
      'theme-act-3',
      'theme-act-4',
      'theme-endgame',
      'restored-fantasy',
    );
    if (store.getState().meta.restoredFantasy) {
      document.body.classList.add('theme-act-1', 'restored-fantasy');
    } else {
      document.body.classList.add(
        act === 'endgame' ? 'theme-endgame' : `theme-act-${act}`,
      );
    }
  }
  private phiLabel(act: number | string): string {
    if (act === 1) return 'Φ';
    return this.langCode === 'pt-br' ? 'Contradições' : 'Contradictions';
  }
  private toDec(v: string): Decimal {
    return new Decimal(v || '0');
  }

  // ── interactions ──────────────────────────────────────────────────────
  private engage(id: string): void {
    initAudio();
    clickSound();
    store.getState().clickEncounter(id);
  }
  private hireSteward(id: string): void {
    store.getState().hireSteward(id);
  }
  private doRealign(): void {
    store.getState().realign();
    this.realignOpen = false;
    save();
  }
  private selectTab(v: Tab): void {
    this.tab = v;
    if (v === 'market') store.getState().openMarket();
  }

  private addEditorInput(): void {
    if (this.editorInputs.length >= 3) return;
    this.editorInputs = [...this.editorInputs, { id: 'wood', amount: 1 }];
  }
  private removeEditorInput(i: number): void {
    if (this.editorInputs.length <= 1) return;
    this.editorInputs = this.editorInputs.filter((_, idx) => idx !== i);
  }
  private setEditorInput(i: number, patch: Partial<{ id: ResourceId; amount: number }>): void {
    this.editorInputs = this.editorInputs.map((inp, idx) =>
      idx === i ? { ...inp, ...patch } : inp,
    );
  }
  private setEditorOutput(patch: Partial<{ id: ResourceId; amount: number }>): void {
    this.editorOutput = { ...this.editorOutput, ...patch };
  }
  private resetEditor(): void {
    this.editorInputs = [{ id: 'wood', amount: 1 }];
    this.editorOutput = { id: 'plank', amount: 1 };
    this.editorError = '';
  }
  private submitEditor(): void {
    const r = store.getState().defineRecipe({
      inputs: this.editorInputs,
      output: this.editorOutput,
    });
    if (r.ok) {
      this.editorOpen = false;
      this.resetEditor();
    } else {
      const isPt = this.langCode === 'pt-br';
      const msgs: Record<string, { en: string; pt: string }> = {
        not_in_act_3: {
          en: 'The Engine does not accept authorship yet.',
          pt: 'A Máquina ainda não aceita autoria.',
        },
        max_recipes: {
          en: 'No more authorship slots remain.',
          pt: 'Não há mais espaços de autoria.',
        },
        no_inputs: { en: 'Recipe needs at least one input.', pt: 'Receita precisa de pelo menos uma entrada.' },
        no_output: { en: 'Recipe needs an output.', pt: 'Receita precisa de uma saída.' },
        value_ratio: {
          en: 'Output value exceeds inputs by too much. The Engine refuses.',
          pt: 'O valor da saída excede demais o das entradas. A Máquina recusa.',
        },
        insufficient_phi: { en: 'Insufficient Φ to author.', pt: 'Φ insuficiente para autorar.' },
      };
      const m = msgs[r.reason];
      this.editorError = m ? (isPt ? m.pt : m.en) : r.reason;
    }
  }
  private craft(id: string, count: number): void {
    store.getState().queueCraft(id, count);
  }
  private cancelCraft(id: string): void {
    store.getState().cancelOneCraft(id);
  }
  private sell(id: ResourceId, amount: number): void {
    store.getState().sell(id, amount);
  }
  private dismissBeat(id: string): void {
    store.getState().dismissBeat(id);
  }
  private confirmReset(): void {
    const msg =
      this.langCode === 'pt-br'
        ? 'Apagar o save e recomeçar? Não há volta.'
        : 'Erase save and begin again? There is no undo.';
    if (confirm(msg)) {
      store.getState().reset();
      save();
    }
  }
  private exportSave(): void {
    save();
    const raw = localStorage.getItem('tpe.save.v1') ?? '';
    const enc = btoa(unescape(encodeURIComponent(raw)));
    navigator.clipboard?.writeText(enc).catch(() => undefined);
    prompt(
      this.langCode === 'pt-br'
        ? 'Save copiado. Guarde esta string:'
        : 'Save copied. Keep this string:',
      enc,
    );
  }
  private importSave(): void {
    const enc = prompt(
      this.langCode === 'pt-br' ? 'Cole o save:' : 'Paste the save string:',
    );
    if (!enc) return;
    try {
      const raw = decodeURIComponent(escape(atob(enc.trim())));
      localStorage.setItem('tpe.save.v1', raw);
      load();
    } catch {
      alert(this.langCode === 'pt-br' ? 'Save inválido.' : 'Invalid save.');
    }
  }

  // ── render ────────────────────────────────────────────────────────────
  render(): TemplateResult {
    const s = store.getState();
    void this.tick;
    if (this.complianceScrollActive) return this.renderComplianceScroll(s);
    if (s.meta.committed) return this.renderEndingScreen(s);
    return html`
      <div class="relative z-10 h-full w-full overflow-y-auto">
        ${this.renderHud(s)} ${this.renderNarrator(s)} ${this.renderScene(s)}
        ${this.renderTabs(s)} ${this.renderPanels(s)} ${this.renderFooter()}
        ${this.ledgerOpen ? this.renderLedger(s) : nothing}
        ${this.realignOpen ? this.renderRealignModal(s) : nothing}
        ${this.editorOpen ? this.renderRecipeEditor(s) : nothing}
        ${this.axiomsOpen ? this.renderAxiomsModal(s) : nothing}
        ${this.recompileOpen ? this.renderRecompileModal(s) : nothing}
        ${this.equilibriumOpen ? this.renderEquilibriumModal(s) : nothing}
        ${this.creditsOpen ? this.renderCreditsModal() : nothing}
        ${this.statsOpen ? this.renderStatsModal(s) : nothing}
        ${this.beatsOpen ? this.renderBeatsModal(s) : nothing}
      </div>
    `;
  }

  private renderStatsModal(
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    const isPt = this.langCode === 'pt-br';
    const playSec = Math.floor(s.meta.totalPlaySec);
    const h = Math.floor(playSec / 3600);
    const m = Math.floor((playSec % 3600) / 60);
    const sec = playSec % 60;
    const time =
      h > 0 ? `${h}h ${m}m ${sec}s` : m > 0 ? `${m}m ${sec}s` : `${sec}s`;
    const rows: { label: { en: string; pt: string }; v: string }[] = [
      {
        label: { en: 'Time played', pt: 'Tempo jogado' },
        v: time,
      },
      {
        label: { en: 'Manual clicks', pt: 'Cliques manuais' },
        v: s.meta.totalClicks.toLocaleString(),
      },
      {
        label: { en: 'Encounter completions', pt: 'Conclusões de jornada' },
        v: fmtInt(s.totalCompletions),
      },
      {
        label: { en: 'Best Φ in a single run', pt: 'Melhor Φ numa run' },
        v: fmtPhi(this.toDec(s.meta.bestPhiEver)),
      },
      {
        label: { en: 'Lifetime Φ (current cycle)', pt: 'Φ vitalício (ciclo atual)' },
        v: fmtPhi(this.toDec(s.meta.lifetimePhi)),
      },
      { label: { en: 'Frames', pt: 'Frames' }, v: String(s.meta.frames) },
      {
        label: { en: 'Realigns', pt: 'Realinhamentos' },
        v: String(s.meta.realignCount),
      },
      {
        label: { en: 'Recompiles', pt: 'Recompilações' },
        v: String(s.meta.recompileCount),
      },
      {
        label: { en: 'Cycles Witnessed', pt: 'Ciclos Testemunhados' },
        v: String(s.meta.cyclesWitnessed),
      },
      {
        label: { en: 'Axiom points', pt: 'Pontos de axioma' },
        v: String(s.meta.axiomPoints),
      },
      {
        label: { en: 'Axioms adopted', pt: 'Axiomas adotados' },
        v: `${s.meta.axioms.length}/13`,
      },
      {
        label: { en: 'User recipes authored', pt: 'Receitas autorais' },
        v: `${s.userRecipes.length}/12`,
      },
    ];
    return html`
      <div
        role="dialog"
        aria-modal="true"
        class="fixed inset-0 z-40 flex items-center justify-center backdrop-blur-md"
        style="background: rgba(10,8,16,0.6);"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this.statsOpen = false;
        }}
      >
        <div
          class="m-4 max-w-md w-full card animate-fade-in"
          style="background: var(--bg-2); border-color: var(--accent);"
        >
          <header
            class="p-4 border-b flex justify-between items-baseline"
            style="border-color: var(--line)"
          >
            <h2 class="title text-lg">${isPt ? 'Estatísticas' : 'Stats'}</h2>
            <button class="btn-mini" @click=${() => (this.statsOpen = false)}>
              ${isPt ? 'fechar' : 'close'}
            </button>
          </header>
          <div class="p-4 space-y-1 text-sm font-mono">
            ${rows.map(
              (r) => html`
                <div
                  class="flex justify-between py-1 border-b last:border-0"
                  style="border-color: var(--line)"
                >
                  <span class="opacity-70">
                    ${isPt ? r.label.pt : r.label.en}
                  </span>
                  <span style="color: var(--accent)">${r.v}</span>
                </div>
              `,
            )}
          </div>
        </div>
      </div>
    `;
  }

  private renderBeatsModal(
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    const isPt = this.langCode === 'pt-br';
    const total = NARRATOR_BEATS_REF.length;
    const fired = s.meta.firedBeats.filter((id) =>
      NARRATOR_BEATS_REF.some((b) => b.id === id),
    ).length;
    return html`
      <div
        role="dialog"
        aria-modal="true"
        class="fixed inset-0 z-40 flex items-center justify-center backdrop-blur-md"
        style="background: rgba(10,8,16,0.6);"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this.beatsOpen = false;
        }}
      >
        <div
          class="m-4 max-w-xl w-full card animate-fade-in flex flex-col"
          style="background: var(--bg-2); border-color: var(--accent); max-height: 88vh;"
        >
          <header
            class="p-4 border-b flex justify-between items-baseline"
            style="border-color: var(--line)"
          >
            <h2 class="title text-lg">
              ${isPt ? 'Marcos' : 'Beats'}
              <span class="font-mono text-xs opacity-60 ml-2"
                >${fired}/${total}</span
              >
            </h2>
            <button class="btn-mini" @click=${() => (this.beatsOpen = false)}>
              ${isPt ? 'fechar' : 'close'}
            </button>
          </header>
          <div class="p-4 space-y-2 overflow-y-auto">
            ${NARRATOR_BEATS_REF.map((b) => {
              const seen = s.meta.firedBeats.includes(b.id);
              return html`
                <div
                  class="border-l-2 pl-3 py-1.5"
                  style=${seen
                    ? 'border-color: var(--accent);'
                    : 'border-color: var(--ink-soft); opacity: 0.45;'}
                >
                  <div class="font-mono text-[10px] uppercase tracking-widest opacity-60">
                    ${b.id}
                  </div>
                  <div class="italic text-sm">
                    ${seen ? (isPt ? b.text.pt : b.text.en) : '???'}
                  </div>
                </div>
              `;
            })}
          </div>
        </div>
      </div>
    `;
  }

  private renderCreditsModal(): TemplateResult {
    return html`
      <div
        role="dialog"
        aria-modal="true"
        class="fixed inset-0 z-40 flex items-center justify-center backdrop-blur-md"
        style="background: rgba(10,8,16,0.6);"
        role="dialog"
        aria-modal="true"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this.creditsOpen = false;
        }}
      >
        <div
          class="m-4 max-w-lg w-full card animate-fade-in"
          style="background: var(--bg-2); border-color: var(--accent);"
        >
          <header
            class="p-5 border-b flex justify-between items-baseline"
            style="border-color: var(--line)"
          >
            <h2 class="title text-lg" style="color: var(--ink)">
              ${t('credits.title')}
            </h2>
            <button
              class="btn-mini"
              @click=${() => (this.creditsOpen = false)}
              aria-label="close"
            >
              ✕
            </button>
          </header>
          <div class="p-5 space-y-3 text-sm">
            <p class="italic opacity-85">${t('credits.intro')}</p>
            <p class="font-mono text-xs opacity-80">${t('credits.code')}</p>
            <p class="font-mono text-xs opacity-80">${t('credits.fonts')}</p>
            <p class="font-mono text-xs opacity-80">${t('credits.art')}</p>
            <p class="font-mono text-xs opacity-80">${t('credits.host')}</p>
            <div class="divider-ornate my-3">·</div>
            <p class="italic text-center opacity-75">${t('credits.thanks')}</p>
          </div>
        </div>
      </div>
    `;
  }

  private renderEquilibriumModal(
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    const isPt = this.langCode === 'pt-br';
    const hasSword = s.resources.swordOfFirstCut.gte(1);
    const hasMirror = s.userRecipes.some((u) => u.mirror);
    const paradoxReady = hasSword && hasMirror;
    return html`
      <div
        role="dialog"
        aria-modal="true"
        class="fixed inset-0 z-40 flex items-center justify-center backdrop-blur-md"
        style="background: rgba(0,0,0,0.85);"
      >
        <div
          class="m-4 max-w-2xl w-full animate-fade-in card"
          style="background: var(--bg-2); border-color: var(--ether);"
        >
          <header class="p-6 border-b text-center" style="border-color: var(--line)">
            <h2 class="title text-3xl tracking-widest" style="color: var(--ether)">
              ${isPt ? 'Equilíbrio' : 'Equilibrium'}
            </h2>
            <p class="text-sm italic opacity-80 mt-3">
              ${isPt
                ? 'A Máquina não requer mais o mundo. Você decide.'
                : 'The Engine no longer requires the world. You decide.'}
            </p>
          </header>
          <div class="p-6 space-y-4">
            <button
              class="card w-full text-left hover:translate-y-[-1px] transition-transform"
              style="border-color: var(--accent);"
              @click=${() => this.chooseEnding('A')}
            >
              <div class="title text-base">${isPt ? 'Cumprir' : 'Commit'}</div>
              <p class="text-sm italic opacity-75 mt-1">
                ${isPt
                  ? 'Alimentar a Máquina até completar. O mundo será processado.'
                  : 'Feed the Engine until it completes. The world is processed.'}
              </p>
            </button>
            <button
              class="card w-full text-left hover:translate-y-[-1px] transition-transform"
              style="border-color: var(--ink-soft);"
              @click=${() => (this.equilibriumOpen = false)}
            >
              <div class="title text-base">${isPt ? 'Recusar' : 'Refuse'}</div>
              <p class="text-sm italic opacity-75 mt-1">
                ${isPt
                  ? 'Não pressione COMMIT. Em 24 horas reais, a Máquina cessa por si.'
                  : 'Do not press COMMIT. In 24 real hours, the Engine ceases on its own.'}
              </p>
            </button>
            <button
              class="card w-full text-left transition-transform"
              style=${paradoxReady
                ? 'border-color: var(--ember); cursor: pointer;'
                : 'border-color: var(--line); opacity: 0.4; cursor: not-allowed;'}
              ?disabled=${!paradoxReady}
              @click=${() => {
                if (paradoxReady) this.chooseEnding('C');
              }}
            >
              <div class="title text-base">${isPt ? 'Paradoxo' : 'Paradox'}</div>
              <p class="text-sm italic opacity-75 mt-1">
                ${paradoxReady
                  ? isPt
                    ? 'Alimente a Máquina com a Espada do Primeiro Corte e uma receita espelho. Ela não conseguirá resolver.'
                    : 'Feed the Engine the Sword of First Cut and a mirror recipe. It cannot resolve.'
                  : isPt
                    ? 'Bloqueado — precisa da Espada do Primeiro Corte E pelo menos uma receita espelho.'
                    : 'Locked — needs the Sword of First Cut AND at least one mirror recipe.'}
              </p>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private chooseEnding(kind: 'A' | 'B' | 'C'): void {
    if (kind === 'A') {
      // Play the compliance scroll cinematic, then commit.
      this.equilibriumOpen = false;
      this.complianceScrollActive = true;
      // Total animation: 22s scroll + 4s whiteout - 18s overlap = ~22s.
      setTimeout(() => {
        store.getState().commitEnding('A');
        this.complianceScrollActive = false;
        save();
      }, 22000);
      return;
    }
    store.getState().commitEnding(kind);
    this.equilibriumOpen = false;
    save();
  }

  private renderComplianceScroll(
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    const isPt = this.langCode === 'pt-br';
    const lines = s.ledger
      .slice()
      .reverse()
      .map((e) => {
        const t = new Date(e.t).toISOString().slice(11, 19);
        const text = isPt ? e.pt : e.en;
        const phi = e.phi !== 0 ? `  ${e.phi > 0 ? '+' : ''}${e.phi.toFixed(1)} Φ` : '';
        return `${t}  ${text}${phi}`;
      })
      .join('\n');
    return html`
      <div class="compliance-scroll">
        <div class="compliance-track">
${lines}

──

${isPt ? 'fim do livro-razão.' : 'end of ledger.'}
        </div>
        <div class="compliance-whiteout"></div>
      </div>
    `;
  }

  private renderEndingScreen(
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    const isPt = this.langCode === 'pt-br';
    const k = s.meta.committed!;
    const titleEn = k === 'A' ? 'Compliance' : k === 'B' ? 'Refusal' : 'Paradox';
    const titlePt = k === 'A' ? 'Cumprimento' : k === 'B' ? 'Recusa' : 'Paradoxo';
    const lineEn =
      k === 'A'
        ? 'The paradox is resolved. Thank you for your contribution.'
        : k === 'B'
          ? 'Acknowledged. Cease.'
          : 'Insufficient grounds. Restart pending.';
    const linePt =
      k === 'A'
        ? 'O paradoxo está resolvido. Obrigada pela sua contribuição.'
        : k === 'B'
          ? 'Confirmado. Cesse.'
          : 'Fundamento insuficiente. Reinício pendente.';
    return html`
      <div
        class="relative z-10 w-full h-full flex flex-col items-center justify-center px-6 text-center"
        style=${k === 'A'
          ? 'background: radial-gradient(ellipse at center, #ffffff, #f0e9d6 50%, #d8c896);'
          : k === 'C'
            ? 'background: radial-gradient(ellipse at center, #1a0f1a, #060306 70%);'
            : ''}
      >
        <div
          class="animate-fade-in max-w-2xl"
          style=${k === 'A' ? 'color:#1a0e08' : ''}
        >
          <div class="divider-ornate w-72 mx-auto mb-6">
            · ${isPt ? `Final ${k}` : `Ending ${k}`} ·
          </div>
          <h1 class="title text-3xl md:text-5xl mb-6">
            ${isPt ? titlePt : titleEn}
          </h1>
          <p class="italic text-lg md:text-xl mb-10 opacity-85">
            "${isPt ? linePt : lineEn}"
          </p>
          <div class="font-mono text-xs opacity-70 mb-10">
            ${isPt ? 'ciclos testemunhados' : 'cycles witnessed'}:
            ${s.meta.cyclesWitnessed}
          </div>
          <div class="flex flex-wrap items-center justify-center gap-3">
            <button class="btn btn-corners" @click=${() => this.doWitness()}>
              ${isPt ? 'testemunhar' : 'witness'}
            </button>
            <button class="btn-mini" @click=${() => (this.ledgerOpen = true)}>
              ${isPt ? 'livro-razão' : 'ledger'}
            </button>
            <div class="lang-toggle ml-2">
              <button
                aria-pressed=${this.langCode === 'en'}
                @click=${() => setLang('en')}
              >
                EN
              </button>
              <span aria-hidden="true" class="opacity-30">·</span>
              <button
                aria-pressed=${this.langCode === 'pt-br'}
                @click=${() => setLang('pt-br')}
              >
                PT-BR
              </button>
            </div>
          </div>
          <p class="mt-6 text-xs opacity-50 font-mono">
            ${isPt
              ? 'Testemunhar inicia um novo ciclo, com a contagem preservada.'
              : 'Witnessing starts a new cycle, with the count preserved.'}
          </p>
        </div>
        ${this.ledgerOpen ? this.renderLedger(s) : nothing}
      </div>
    `;
  }

  private doWitness(): void {
    store.getState().witness();
    save();
  }

  private renderHud(s: ReturnType<typeof store.getState>): TemplateResult {
    const isPt = this.langCode === 'pt-br';
    const showLedger = s.act !== 1;
    const showRealign = s.act === 3 || s.act === 4 || s.act === 'endgame';
    const showRecompile = s.act === 4 || s.act === 'endgame';
    const label = this.phiLabel(s.act);
    const earned = s.act === 3 || s.act === 4 ? framesEarned(s.phi) : 0;
    const apEarned = showRecompile
      ? axiomPointsEarned(this.toDec(s.meta.lifetimePhi))
      : 0;
    return html`
      <header class="sticky top-0 z-30 backdrop-blur-md hud-bg">
        <div class="max-w-6xl mx-auto px-4 md:px-8 pt-3 pb-2 flex items-center gap-4">
          <h1
            class="title text-base md:text-lg shrink-0"
            style="color:var(--ink); letter-spacing: 0.24em"
          >
            ${t('landing.title')}
          </h1>
          <div class="flex-1 overflow-x-auto">
            <div class="flex items-center gap-3 md:gap-5 whitespace-nowrap">
              ${RES_DISPLAY.map((id) => this.renderResourceChip(id, s))}
              ${s.meta.frames > 0
                ? html`
                    <div
                      class="flex items-center gap-1.5 font-ui text-sm"
                      title=${isPt
                        ? `Frames acumulados — ×${frameMultiplier(s.meta.frames).toFixed(2)} produção`
                        : `Lifetime Frames — ×${frameMultiplier(s.meta.frames).toFixed(2)} production`}
                    >
                      <span style="color: var(--accent)">⊞</span>
                      <span class="font-mono">${s.meta.frames}</span>
                      <span class="opacity-50 hidden md:inline"
                        >${isPt ? 'Frames' : 'Frames'}</span
                      >
                    </div>
                  `
                : nothing}
              ${s.meta.cyclesWitnessed > 0
                ? html`
                    <div
                      class="flex items-center gap-1.5 font-ui text-sm"
                      title=${isPt
                        ? 'Ciclos testemunhados (NG+)'
                        : 'Cycles Witnessed (NG+)'}
                    >
                      <span style="color: var(--ether)">◯</span>
                      <span class="font-mono">${s.meta.cyclesWitnessed}</span>
                      <span class="opacity-50 hidden md:inline"
                        >${isPt ? 'Cyc' : 'Cyc'}</span
                      >
                    </div>
                  `
                : nothing}
            </div>
          </div>
          ${showRealign
            ? html`
                <button
                  class="btn-mini shrink-0 realign-btn animate-fade-in"
                  ?disabled=${earned <= 0}
                  @click=${() => (this.realignOpen = true)}
                  title=${isPt
                    ? `Realinhar — ganhar ${earned} Frames`
                    : `Realign — gain ${earned} Frames`}
                >
                  ${isPt ? 'realinhar' : 'realign'}${earned > 0 ? ` +${earned}` : ''}
                </button>
              `
            : nothing}
          ${showRecompile
            ? html`
                ${s.meta.equilibriumAt > 0 && !s.meta.committed
                  ? html`
                      <button
                        class="btn-mini shrink-0 realign-btn animate-fade-in"
                        @click=${() => (this.equilibriumOpen = true)}
                        title=${isPt ? 'Equilíbrio atingido' : 'Equilibrium reached'}
                        style="border-color: var(--ember); color: var(--ember);"
                      >
                        ${isPt ? 'equilíbrio' : 'equilibrium'}
                      </button>
                    `
                  : html`
                      <button
                        class="btn-mini shrink-0 realign-btn animate-fade-in"
                        ?disabled=${apEarned <= 0}
                        @click=${() => (this.recompileOpen = true)}
                        title=${isPt
                          ? `Recompile — ganhar ${apEarned} pontos de axioma`
                          : `Recompile — gain ${apEarned} axiom points`}
                      >
                        ${isPt ? 'recompilar' : 'recompile'}${apEarned > 0 ? ` +${apEarned}` : ''}
                      </button>
                    `}
                <button
                  class="btn-mini shrink-0 realign-btn animate-fade-in"
                  @click=${() => (this.axiomsOpen = true)}
                  title=${isPt ? 'Axiomas' : 'Axioms'}
                >
                  ${isPt ? 'axiomas' : 'axioms'}${s.meta.axiomPoints > 0 ? ` · ${s.meta.axiomPoints}` : ''}
                </button>
              `
            : nothing}
          ${showLedger
            ? html`
                <button
                  class="btn-mini shrink-0 ledger-btn animate-fade-in"
                  @click=${() => (this.ledgerOpen = true)}
                  title=${isPt ? 'Abrir livro-razão' : 'Open ledger'}
                >
                  [ ${isPt ? 'razão' : 'ledger'} ]
                </button>
              `
            : nothing}
          ${s.act === 1
            ? html`
                <div class="phi shrink-0 flex items-baseline gap-1">
                  <span class="font-mono">Φ ${fmtPhi(s.phi)}</span>
                </div>
              `
            : this.renderContradictionsHud(s, label)}
        </div>
      </header>
    `;
  }

  private renderContradictionsHud(
    s: ReturnType<typeof store.getState>,
    label: string,
  ): TemplateResult {
    const showClasses = s.act === 3 || s.act === 4 || s.act === 'endgame';
    if (!showClasses) {
      return html`
        <div
          class="phi shrink-0 flex items-baseline gap-1"
          style="opacity:1; color: var(--accent);"
        >
          <span class="font-mono text-xs uppercase tracking-widest opacity-70">
            ${label}
          </span>
          <span class="font-mono">${fmtPhi(s.phi)}</span>
        </div>
      `;
    }
    // Compact split for Act III+: a single chip cluster
    const classes: { id: ContradictionClass; sym: string; tip: string }[] = [
      { id: 'conservation', sym: 'C', tip: 'Conservation' },
      { id: 'identity', sym: 'I', tip: 'Identity' },
      { id: 'causal', sym: 'K', tip: 'Causal' },
      { id: 'semantic', sym: 'S', tip: 'Semantic' },
    ];
    return html`
      <div
        class="hidden md:flex shrink-0 items-baseline gap-2 font-mono text-xs"
        title=${label}
        style="color: var(--accent);"
      >
        ${classes.map(
          (c) => html`
            <span class="flex items-baseline gap-1" title=${c.tip}>
              <span class="opacity-65">${c.sym}</span>
              <span>${fmtPhi(s.contradictions[c.id])}</span>
            </span>
          `,
        )}
      </div>
      <div
        class="md:hidden phi shrink-0 flex items-baseline gap-1"
        style="opacity:1; color: var(--accent);"
      >
        <span class="font-mono">${fmtPhi(s.phi)}</span>
      </div>
    `;
  }

  private renderResourceChip(
    id: ResourceId,
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    const value = s.resources[id];
    if (id !== 'coin' && id !== 'xp' && value.lte(0)) return html``;
    return html`
      <div
        class="flex items-center gap-1.5 font-ui text-sm md:text-base"
        title=${this.resourceName(id)}
      >
        <span class="text-[0.95em]" style="color:var(--accent)"
          >${RESOURCE_ICON[id] ?? '·'}</span
        >
        <span class="font-mono tabular-nums" style="color:var(--ink)"
          >${fmtInt(value)}</span
        >
        <span class="opacity-50 hidden md:inline">${this.resourceName(id)}</span>
      </div>
    `;
  }

  private renderNarrator(s: ReturnType<typeof store.getState>): TemplateResult {
    if (s.narratorQueue.length === 0) return html``;
    const isPt = this.langCode === 'pt-br';
    return html`
      <div class="max-w-3xl mx-auto px-4 md:px-8 mt-2 space-y-2">
        ${repeat(
          s.narratorQueue,
          (b) => b.id,
          (b) => html`
            <div
              class="animate-fade-in border-l-2 pl-4 pr-6 py-3 flex items-start gap-3"
              style="border-color: var(--accent); background: rgba(255,240,200,0.35);"
            >
              <span class="font-title text-xs tracking-widest opacity-60 mt-0.5"
                >${isPt ? 'Conselheiro' : 'Counsel'}</span
              >
              <p class="italic flex-1" style="color:var(--ink)">
                ${isPt ? b.pt : b.en}
              </p>
              <button
                class="opacity-60 hover:opacity-100 px-1"
                @click=${() => this.dismissBeat(b.id)}
                title=${isPt ? 'Dispensar' : 'Dismiss'}
              >
                ✕
              </button>
            </div>
          `,
        )}
      </div>
    `;
  }

  private renderScene(s: ReturnType<typeof store.getState>): TemplateResult {
    const showSchematic = s.act === 3 || s.act === 4 || s.act === 'endgame';
    return html`
      <div class="relative">
        ${this.renderCastleScene()}
        ${showSchematic ? this.renderEngineOverlay(s) : nothing}
      </div>
    `;
  }

  private renderEngineOverlay(
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    // Diagram-style overlay: 5 source nodes feed a central reactor.
    const isPt = this.langCode === 'pt-br';
    const nodes: { id: ContradictionClass; label: string; x: number; y: number }[] = [
      { id: 'conservation', label: 'Conservation', x: 130, y: 100 },
      { id: 'identity', label: 'Identity', x: 670, y: 100 },
      { id: 'causal', label: 'Causal', x: 80, y: 240 },
      { id: 'semantic', label: 'Semantic', x: 720, y: 240 },
      { id: 'reflexive', label: 'Reflexive', x: 400, y: 60 },
    ];
    return html`
      <div
        class="absolute inset-0 pointer-events-none animate-fade-in"
        style="mix-blend-mode: screen; opacity: 0.85;"
      >
        <div class="relative max-w-5xl mx-auto px-4 md:px-8 h-full">
          <div
            class="absolute inset-x-4 md:inset-x-8 top-0 bottom-0 rounded-sm"
            style="border: 1px solid var(--ether); box-shadow: 0 0 60px rgba(124,92,255,0.18) inset;"
          >
            <svg viewBox="0 0 800 360" class="w-full h-full">
              <defs>
                <radialGradient id="reactor" cx="0.5" cy="0.5" r="0.5">
                  <stop offset="0%" stop-color="#fff" stop-opacity="0.9" />
                  <stop offset="35%" stop-color="#7c5cff" stop-opacity="0.6" />
                  <stop offset="100%" stop-color="#7c5cff" stop-opacity="0" />
                </radialGradient>
              </defs>

              <!-- Pipes (CSS-animated; honors prefers-reduced-motion) -->
              ${nodes.map((n, i) => {
                const v = s.contradictions[n.id];
                const speedClass = v.gt(100) ? 'fast' : v.gt(10) ? '' : 'slow';
                return html`
                  <path
                    class="reactor-pipe ${speedClass}"
                    d="M ${n.x} ${n.y} Q 400 200 400 200"
                    stroke="#7c5cff"
                    stroke-width="1.2"
                    fill="none"
                    opacity="${v.gt(0) ? 0.65 : 0.25}"
                    style="animation-delay: ${i * 0.3}s"
                  />
                `;
              })}

              <!-- Source nodes -->
              ${nodes.map((n) => {
                const v = s.contradictions[n.id];
                const active = v.gt(0);
                return html`
                  <g transform="translate(${n.x} ${n.y})">
                    <rect
                      x="-50"
                      y="-12"
                      width="100"
                      height="24"
                      fill="rgba(10,10,13,0.85)"
                      stroke="#7c5cff"
                      stroke-width="${active ? 1.4 : 0.8}"
                      opacity="${active ? 1 : 0.5}"
                    />
                    <text
                      x="0"
                      y="4"
                      font-family="JetBrains Mono"
                      font-size="10"
                      text-anchor="middle"
                      fill="#e6e1f5"
                    >
                      ${n.label} · ${fmtPhi(v)}
                    </text>
                  </g>
                `;
              })}

              <!-- Central reactor (CSS-animated) -->
              <circle class="reactor-core" cx="400" cy="200" r="60" fill="url(#reactor)" />
              <circle
                cx="400"
                cy="200"
                r="34"
                fill="none"
                stroke="#7c5cff"
                stroke-width="1.2"
              />
              <text
                x="400"
                y="204"
                font-family="JetBrains Mono"
                font-size="12"
                text-anchor="middle"
                fill="#e6e1f5"
              >
                ${isPt ? 'reator' : 'reactor'}
              </text>
              <text
                x="400"
                y="220"
                font-family="JetBrains Mono"
                font-size="10"
                text-anchor="middle"
                fill="#e6e1f5"
                opacity="0.7"
              >
                Φ ${fmtPhi(s.phi)}
              </text>
            </svg>
          </div>
        </div>
      </div>
    `;
  }

  private renderCastleScene(): TemplateResult {
    // Single SVG illuminated tableau, viewBox-based so it scales cleanly.
    return html`
      <div
        class="relative max-w-5xl mx-auto px-4 md:px-8 mt-6 mb-4 select-none scene-castle-wrap"
        aria-hidden="true"
      >
        <div
          class="relative overflow-hidden rounded-sm"
          style="
            background:
              radial-gradient(80% 90% at 50% 100%, rgba(255,205,140,0.35), transparent 70%),
              linear-gradient(180deg, rgba(255,235,200,0.0) 0%, rgba(255,225,180,0.35) 60%, rgba(176,141,87,0.18) 100%);
            border: 1px solid var(--line);
            box-shadow: var(--shadow);
          "
        >
          <svg
            viewBox="0 0 800 360"
            preserveAspectRatio="xMidYMid slice"
            class="block w-full h-44 md:h-64 lg:h-72"
          >
            <defs>
              <radialGradient id="sun" cx="0.5" cy="0.5" r="0.5">
                <stop offset="0%" stop-color="#fff3d6" stop-opacity="1" />
                <stop offset="40%" stop-color="#ffd58c" stop-opacity="0.85" />
                <stop offset="100%" stop-color="#ffb066" stop-opacity="0" />
              </radialGradient>
              <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#fff2d2" stop-opacity="0.7" />
                <stop offset="100%" stop-color="#f1e0b5" stop-opacity="0" />
              </linearGradient>
              <linearGradient id="keep" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#5a3a26" />
                <stop offset="100%" stop-color="#3a241a" />
              </linearGradient>
              <linearGradient id="keepShade" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stop-color="#000" stop-opacity="0.18" />
                <stop offset="100%" stop-color="#000" stop-opacity="0" />
              </linearGradient>
              <linearGradient id="banner" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#c89a5d" />
                <stop offset="100%" stop-color="#a17a45" />
              </linearGradient>
            </defs>

            <!-- Sky wash -->
            <rect x="0" y="0" width="800" height="280" fill="url(#sky)" />

            <!-- Sun + rays -->
            <g transform="translate(620 80)">
              <circle r="58" fill="url(#sun)">
                <animate
                  attributeName="r"
                  values="56;62;56"
                  dur="7s"
                  repeatCount="indefinite"
                />
              </circle>
              <circle r="26" fill="#fff4d6" opacity="0.9" />
            </g>

            <!-- Distant hills (far) -->
            <path
              d="M0 240 Q 120 200 240 220 T 480 215 T 720 225 T 800 220 L 800 360 L 0 360 Z"
              fill="#caa676"
              opacity="0.55"
            />
            <!-- Middle hills -->
            <path
              d="M0 270 Q 160 230 320 255 T 600 250 T 800 260 L 800 360 L 0 360 Z"
              fill="#a8804f"
              opacity="0.65"
            />
            <!-- Foreground hill (anchors the castle) -->
            <path
              d="M0 320 Q 200 270 400 300 Q 600 330 800 300 L 800 360 L 0 360 Z"
              fill="#7a4f2e"
              opacity="0.85"
            />

            <!-- Distant village (tiny rooftops) -->
            <g fill="#8a5e35" opacity="0.55" transform="translate(110 248)">
              <polygon points="0,0 10,-6 20,0 20,8 0,8" />
              <polygon points="22,2 32,-4 42,2 42,8 22,8" />
              <polygon points="46,0 58,-7 70,0 70,8 46,8" />
            </g>
            <g fill="#8a5e35" opacity="0.55" transform="translate(660 252)">
              <polygon points="0,0 10,-6 20,0 20,8 0,8" />
              <polygon points="22,2 32,-4 42,2 42,8 22,8" />
            </g>

            <!-- ── Castle, centered, anchored to the foreground hill ────── -->
            <g transform="translate(400 295)">
              <!-- Outer wall -->
              <rect
                x="-130"
                y="-78"
                width="260"
                height="96"
                fill="#4a2f1f"
              />
              <rect
                x="-130"
                y="-78"
                width="260"
                height="96"
                fill="url(#keepShade)"
              />
              <!-- Outer battlements -->
              <g fill="#4a2f1f">
                <rect x="-130" y="-86" width="16" height="10" />
                <rect x="-100" y="-86" width="16" height="10" />
                <rect x="-70" y="-86" width="16" height="10" />
                <rect x="80" y="-86" width="16" height="10" />
                <rect x="110" y="-86" width="16" height="10" />
              </g>

              <!-- Gate house (taller central block) -->
              <rect
                x="-44"
                y="-110"
                width="88"
                height="128"
                fill="url(#keep)"
              />
              <!-- Gatehouse battlements -->
              <g fill="#3a241a">
                <rect x="-44" y="-118" width="14" height="10" />
                <rect x="-22" y="-118" width="14" height="10" />
                <rect x="0" y="-118" width="14" height="10" />
                <rect x="22" y="-118" width="14" height="10" />
                <rect x="30" y="-118" width="14" height="10" />
              </g>

              <!-- Arched gate -->
              <path
                d="M -18 18 V -22 a 18 18 0 0 1 36 0 V 18 Z"
                fill="#1a0e08"
              />
              <!-- Gate glow -->
              <ellipse
                cx="0"
                cy="6"
                rx="14"
                ry="6"
                fill="#ffd58c"
                opacity="0.45"
              >
                <animate
                  attributeName="opacity"
                  values="0.35;0.65;0.35"
                  dur="3.6s"
                  repeatCount="indefinite"
                />
              </ellipse>

              <!-- Towers (left and right) -->
              <g>
                <rect x="-170" y="-100" width="40" height="118" fill="url(#keep)" />
                <g fill="#3a241a">
                  <rect x="-170" y="-108" width="10" height="10" />
                  <rect x="-152" y="-108" width="10" height="10" />
                  <rect x="-140" y="-108" width="10" height="10" />
                </g>
                <!-- Conical roof -->
                <polygon
                  points="-172,-108 -150,-140 -128,-108"
                  fill="#5a3a26"
                />
                <polygon
                  points="-150,-140 -128,-108 -150,-108"
                  fill="#000"
                  opacity="0.18"
                />
                <!-- Window -->
                <rect x="-156" y="-70" width="8" height="14" fill="#ffd58c" opacity="0.85" />
              </g>
              <g>
                <rect x="130" y="-100" width="40" height="118" fill="url(#keep)" />
                <g fill="#3a241a">
                  <rect x="130" y="-108" width="10" height="10" />
                  <rect x="148" y="-108" width="10" height="10" />
                  <rect x="160" y="-108" width="10" height="10" />
                </g>
                <polygon points="128,-108 150,-140 172,-108" fill="#5a3a26" />
                <polygon
                  points="150,-140 172,-108 150,-108"
                  fill="#000"
                  opacity="0.18"
                />
                <rect x="148" y="-70" width="8" height="14" fill="#ffd58c" opacity="0.85" />
              </g>

              <!-- Gatehouse windows -->
              <rect x="-26" y="-86" width="8" height="14" fill="#ffd58c" opacity="0.9" />
              <rect x="18" y="-86" width="8" height="14" fill="#ffd58c" opacity="0.9" />
              <rect x="-26" y="-56" width="8" height="14" fill="#ffd58c" opacity="0.8" />
              <rect x="18" y="-56" width="8" height="14" fill="#ffd58c" opacity="0.8" />

              <!-- Flagpole + banner (attached to the gatehouse) -->
              <line
                x1="0"
                y1="-118"
                x2="0"
                y2="-170"
                stroke="#3a241a"
                stroke-width="2"
              />
              <g transform="translate(2 -168)">
                <g style="transform-origin: 0 0; animation: bannerWave 3.6s ease-in-out infinite;">
                  <path
                    d="M0 0 L46 0 L40 12 L46 26 L0 26 Z"
                    fill="url(#banner)"
                  />
                  <text
                    x="22"
                    y="18"
                    font-family="Cinzel"
                    font-size="14"
                    font-weight="700"
                    text-anchor="middle"
                    fill="#2a1a10"
                    opacity="0.85"
                  >
                    Φ
                  </text>
                </g>
              </g>

              <!-- Path leading away from the gate -->
              <path
                d="M -10 18 Q -40 36 -120 60 Q -260 90 -400 80"
                stroke="#a8804f"
                stroke-width="6"
                fill="none"
                opacity="0.55"
                stroke-linecap="round"
              />
              <path
                d="M 10 18 Q 40 36 120 60 Q 260 90 400 80"
                stroke="#a8804f"
                stroke-width="6"
                fill="none"
                opacity="0.55"
                stroke-linecap="round"
              />
            </g>

            <!-- Torches outside the gate -->
            <g transform="translate(360 315)">
              <rect x="-1" y="-12" width="2" height="14" fill="#3a241a" />
              <circle cx="0" cy="-16" r="3" fill="#ffd58c">
                <animate
                  attributeName="r"
                  values="2.6;3.4;2.6"
                  dur="1.4s"
                  repeatCount="indefinite"
                />
              </circle>
              <circle cx="0" cy="-16" r="10" fill="#ffb066" opacity="0.35">
                <animate
                  attributeName="opacity"
                  values="0.25;0.5;0.25"
                  dur="1.4s"
                  repeatCount="indefinite"
                />
              </circle>
            </g>
            <g transform="translate(440 315)">
              <rect x="-1" y="-12" width="2" height="14" fill="#3a241a" />
              <circle cx="0" cy="-16" r="3" fill="#ffd58c">
                <animate
                  attributeName="r"
                  values="2.6;3.4;2.6"
                  dur="1.7s"
                  begin="0.6s"
                  repeatCount="indefinite"
                />
              </circle>
              <circle cx="0" cy="-16" r="10" fill="#ffb066" opacity="0.35">
                <animate
                  attributeName="opacity"
                  values="0.25;0.5;0.25"
                  dur="1.7s"
                  begin="0.6s"
                  repeatCount="indefinite"
                />
              </circle>
            </g>

            <!-- Drifting birds -->
            <g>
              <path
                d="M 0 0 q 6 -6 12 0 q 6 -6 12 0"
                stroke="#3a241a"
                stroke-width="1.5"
                fill="none"
                opacity="0.55"
              >
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  from="80 90"
                  to="780 70"
                  dur="40s"
                  repeatCount="indefinite"
                />
              </path>
              <path
                d="M 0 0 q 5 -5 10 0 q 5 -5 10 0"
                stroke="#3a241a"
                stroke-width="1.2"
                fill="none"
                opacity="0.45"
              >
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  from="120 115"
                  to="780 95"
                  dur="55s"
                  repeatCount="indefinite"
                />
              </path>
            </g>

            <!-- Subtle clouds -->
            <g fill="#fff" opacity="0.55">
              <ellipse cx="160" cy="80" rx="38" ry="6">
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  from="0 0"
                  to="60 0"
                  dur="32s"
                  repeatCount="indefinite"
                />
              </ellipse>
              <ellipse cx="470" cy="55" rx="50" ry="7">
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  from="0 0"
                  to="50 0"
                  dur="44s"
                  repeatCount="indefinite"
                />
              </ellipse>
            </g>
          </svg>
          <div class="scene-castle-tint"></div>
        </div>
      </div>
    `;
  }

  private renderTabs(s: ReturnType<typeof store.getState>): TemplateResult {
    const isPt = this.langCode === 'pt-br';
    const labels: Record<Tab, string> = {
      encounters: isPt ? 'Jornadas' : 'Encounters',
      stewards: isPt ? 'Mãos' : 'Stewards',
      reactors: isPt ? 'Reatores' : 'Reactors',
      inventory: isPt ? 'Inventário' : 'Inventory',
      crafting: isPt ? 'Ofício' : 'Crafting',
      market: isPt ? 'Mercado' : 'Market',
    };
    const showStewards = s.act !== 1;
    const showMarket = s.act === 3 || s.act === 4 || s.act === 'endgame';
    const showReactors = s.act === 4 || s.act === 'endgame';
    const visible = TAB_VALUES.filter(
      (v) =>
        (v !== 'stewards' || showStewards) &&
        (v !== 'market' || showMarket) &&
        (v !== 'reactors' || showReactors) &&
        (v !== 'inventory' || !showMarket),
    );
    return html`
      <div class="max-w-5xl mx-auto px-4 md:px-8 md:hidden">
        <div class="flex gap-2 border-b" style="border-color: var(--line)">
          ${visible.map(
            (v) => html`
              <button
                class=${classMap({
                  'flex-1 py-3 font-title text-xs tracking-widest uppercase':
                    true,
                  'opacity-100 border-b-2': this.tab === v,
                  'opacity-50': this.tab !== v,
                })}
                style=${this.tab === v ? 'border-color: var(--accent);' : ''}
                @click=${() => this.selectTab(v)}
              >
                ${labels[v]}
              </button>
            `,
          )}
        </div>
      </div>
    `;
  }

  private renderPanels(s: ReturnType<typeof store.getState>): TemplateResult {
    const showStewards = s.act !== 1;
    const showMarket = s.act === 3 || s.act === 4 || s.act === 'endgame';
    const showReactors = s.act === 4 || s.act === 'endgame';
    const colCount =
      2 + (showStewards ? 1 : 0) + (showMarket ? 1 : 0) + (showReactors ? 1 : 0);
    const desktopCols =
      colCount >= 5
        ? 'md:grid-cols-5'
        : colCount === 4
          ? 'md:grid-cols-4'
          : colCount === 3
            ? 'md:grid-cols-3'
            : 'md:grid-cols-2';
    return html`
      <div
        class="max-w-6xl mx-auto px-4 md:px-8 pb-12 mt-4 grid ${desktopCols} gap-4 md:gap-6"
      >
        <section
          class=${classMap({
            'md:block': true,
            hidden: this.tab !== 'encounters',
            block: this.tab === 'encounters',
          })}
        >
          ${this.renderEncountersPanel(s)}
        </section>
        ${showStewards
          ? html`
              <section
                class=${classMap({
                  'md:block': true,
                  hidden: this.tab !== 'stewards',
                  block: this.tab === 'stewards',
                })}
              >
                ${this.renderStewardsPanel(s)}
              </section>
            `
          : nothing}
        ${showReactors
          ? html`
              <section
                class=${classMap({
                  'md:block': true,
                  hidden: this.tab !== 'reactors',
                  block: this.tab === 'reactors',
                })}
              >
                ${this.renderReactorsPanel(s)}
              </section>
            `
          : nothing}
        <section
          class=${classMap({
            'md:block': true,
            hidden: showMarket
              ? this.tab !== 'market'
              : this.tab !== 'inventory',
            block: showMarket
              ? this.tab === 'market'
              : this.tab === 'inventory',
          })}
        >
          ${showMarket ? this.renderMarketPanel(s) : this.renderInventoryPanel(s)}
        </section>
        <section
          class=${classMap({
            'md:block': true,
            hidden: this.tab !== 'crafting',
            block: this.tab === 'crafting',
          })}
        >
          ${this.renderCraftingPanel(s)}
        </section>
      </div>
    `;
  }

  private renderMarketPanel(
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    const isPt = this.langCode === 'pt-br';
    const sellableRows: ResourceId[] = [
      'wood',
      'ore',
      'hide',
      'herb',
      'ether',
      'crystal',
      'silk',
      'bone',
      'plank',
      'ingot',
      'salve',
      'charm',
      'shortSword',
      'cart',
      'reinforcedCharm',
      'elixir',
      'anvilPiece',
      'lantern',
      'tome',
      'reliquary',
      'standard',
    ];
    return html`
      ${this.panelTitle(isPt ? 'Mercado' : 'Market')}
      <p class="text-xs italic opacity-65 mb-3 px-1 font-mono">
        ${isPt
          ? 'Função interna da Máquina. "Vender" é alimentar.'
          : 'An internal Engine routine. "Selling" is feeding.'}
      </p>
      <div class="card">
        <table class="w-full text-sm">
          <thead>
            <tr
              class="text-[10px] uppercase tracking-widest opacity-60 font-mono"
              style="border-bottom: 1px solid var(--line)"
            >
              <th class="text-left py-2 pr-2">${isPt ? 'item' : 'item'}</th>
              <th class="text-right pr-2">${isPt ? 'estoque' : 'stock'}</th>
              <th class="text-right pr-2">${isPt ? 'demanda' : 'demand'}</th>
              <th class="text-right pr-2">${isPt ? 'preço' : 'price'}</th>
              <th class="text-right">${isPt ? 'vender' : 'sell'}</th>
            </tr>
          </thead>
          <tbody>
            ${sellableRows.map((id) => {
              const item = ITEMS[id];
              if (item.sell <= 0) return nothing;
              const amount = s.resources[id];
              if (amount.lte(0)) return nothing;
              void this.tick; // trigger re-render so demand recalculates
              const demand = effectiveMarketDemand(s, id);
              const price = Math.floor(item.sell * demand);
              const demandColor =
                demand >= 1.05
                  ? 'color: #6fd66f'
                  : demand <= 0.95
                    ? 'color: #ff8e6c'
                    : 'opacity: 0.7';
              return html`
                <tr style="border-bottom: 1px solid var(--line)">
                  <td class="py-2 pr-2">
                    <span class="mr-1" style="color:var(--accent)"
                      >${RESOURCE_ICON[id] ?? '·'}</span
                    >
                    ${this.resourceName(id)}
                  </td>
                  <td class="py-2 pr-2 text-right font-mono tabular-nums">
                    ${fmtInt(amount)}
                  </td>
                  <td
                    class="py-2 pr-2 text-right font-mono tabular-nums"
                    style=${demandColor}
                  >
                    ×${demand.toFixed(2)}
                  </td>
                  <td class="py-2 pr-2 text-right font-mono tabular-nums">
                    ${price}${RESOURCE_ICON['coin']}
                  </td>
                  <td class="py-2 text-right">
                    <div class="inline-flex items-center gap-1">
                      <button
                        class="btn-mini"
                        @click=${() => this.marketSell(id, 1)}
                      >
                        ×1
                      </button>
                      <button
                        class="btn-mini"
                        @click=${() => this.marketSell(id, 10)}
                      >
                        ×10
                      </button>
                      <button
                        class="btn-mini"
                        @click=${() => this.marketSell(id, Number.MAX_SAFE_INTEGER)}
                      >
                        ${isPt ? 'tudo' : 'all'}
                      </button>
                    </div>
                  </td>
                </tr>
              `;
            })}
            ${sellableRows.every((id) => s.resources[id].lte(0))
              ? html`<tr>
                  <td class="py-6 text-center opacity-60 italic" colspan="5">
                    ${isPt ? 'Nada a oferecer.' : 'Nothing to offer.'}
                  </td>
                </tr>`
              : nothing}
          </tbody>
        </table>
      </div>
      ${s.resources.swordOfFirstCut.gte(1)
        ? html`
            <div class="mt-3 card flex items-center justify-between" style="border-color: var(--accent);">
              <div>
                <div class="font-title text-sm flex items-center gap-2">
                  <span style="color: var(--accent)">✠</span>
                  ${this.resourceName('swordOfFirstCut')}
                </div>
                <div class="text-[11px] italic opacity-65 mt-0.5">
                  ${isPt
                    ? 'O Mercado não compra esta linha.'
                    : 'The Market does not buy this row.'}
                </div>
              </div>
              <span
                class="text-[10px] uppercase tracking-widest opacity-65 font-mono"
                >${isPt ? 'Preservado' : 'Preserved'}</span
              >
            </div>
          `
        : nothing}
      ${s.meta.flickerFired
        ? html`
            <p class="mt-3 text-[11px] font-mono opacity-50 italic">
              ${isPt
                ? '* Houve um pulso. A linha foi para o livro-razão.'
                : '* There was a flicker. The line went to the ledger.'}
            </p>
          `
        : nothing}
    `;
  }

  private marketSell(id: ResourceId, amount: number): void {
    store.getState().sellAtMarket(id, amount);
  }

  private renderStewardsPanel(
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    const isPt = this.langCode === 'pt-br';
    const phiNum = s.phi.toNumber();
    return html`
      ${this.panelTitle(isPt ? 'Mãos' : 'Stewards')}
      ${this.renderNpcEntreaty(s)}
      <p class="text-xs italic opacity-65 mb-3 px-1">
        ${isPt
          ? 'Eles trabalham. O livro-razão atribui a você.'
          : 'They work. The ledger attributes it to you.'}
      </p>
      <div class="space-y-3">
        ${STEWARDS.map((def) => {
          const stw = s.stewards[def.id];
          if (phiNum < def.unlockPhi) {
            return html`
              <div class="card opacity-60">
                <div class="flex justify-between items-baseline">
                  <h3 class="title text-base">??</h3>
                  <span class="font-mono text-xs opacity-60"
                    >${this.phiLabel(s.act)}
                    ${fmtPhi(s.phi)}/${def.unlockPhi}</span
                  >
                </div>
                <p class="text-sm italic opacity-70 mt-1">
                  ${isPt ? 'Aguardando reconhecimento.' : 'Awaiting recognition.'}
                </p>
              </div>
            `;
          }
          const cost = stewardCost(def, stw.level);
          const canAfford = s.resources.coin.gte(cost);
          const rate = stw.level > 0 ? stw.level / def.cycleSec : 0;
          return html`
            <div class="card">
              <div class="flex justify-between items-baseline">
                <h3 class="title text-base">${this.name(def.name)}</h3>
                <span class="font-mono text-xs opacity-60"
                  >${isPt ? 'nv' : 'lvl'} ${stw.level}</span
                >
              </div>
              <p class="text-sm italic opacity-75 mt-1">
                ${this.name(def.flavor)}
              </p>
              <div class="mt-2 text-xs font-mono opacity-75 flex flex-wrap gap-x-3">
                <span
                  >${isPt ? 'Cuida de' : 'Tends'}:
                  ${this.encounterName(def.encounterId)}</span
                >
                ${stw.level > 0
                  ? html`<span
                      >${rate.toFixed(2)} ${isPt ? 'passos/s' : 'steps/s'}</span
                    >`
                  : nothing}
              </div>
              <div class="mt-3 flex flex-wrap gap-2">
                <button
                  class="btn-mini"
                  ?disabled=${!canAfford}
                  @click=${() => this.hireSteward(def.id)}
                >
                  ${isPt ? 'Contratar' : 'Hire'} · ${cost}${RESOURCE_ICON['coin']}
                </button>
              </div>
            </div>
          `;
        })}
      </div>
    `;
  }

  private encounterRateText(
    def: (typeof ENCOUNTERS)[number],
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    const isPt = this.langCode === 'pt-br';
    const isAct3 = s.act === 3 || s.act === 4 || s.act === 'endgame';
    if (!isAct3) {
      const st = s.encounters[def.id];
      return html`<span
        >${Math.round(st.progress)} / ${def.duration} ${isPt ? 'passos' : 'steps'}</span
      >`;
    }
    // throughput = (auto baseline + steward contribution) / duration cycles per second
    const stewardForEncounter = STEWARDS.find(
      (st) => st.encounterId === def.id,
    );
    let stepsPerSec = 0.4; // ACT3_AUTO_STEPS_PER_SEC mirror
    if (stewardForEncounter) {
      const stw = s.stewards[stewardForEncounter.id];
      if (stw && stw.level > 0) {
        stepsPerSec += stw.level / stewardForEncounter.cycleSec;
      }
    }
    const cyclesPerMin = (stepsPerSec / def.duration) * 60;
    return html`<span
      >${stepsPerSec.toFixed(2)} ${isPt ? 'passos/s' : 'steps/s'} ·
      ${cyclesPerMin.toFixed(1)} ${isPt ? 'ciclos/min' : 'cycles/min'}</span
    >`;
  }

  private renderNpcEntreaty(
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    if (s.meta.npcEntreatyAt === 0) return html``;
    const isPt = this.langCode === 'pt-br';
    const accepted = s.meta.npcAcceptedAt > 0;
    const resolved = s.meta.npcResolved;
    if (resolved) {
      return html`
        <div
          class="card mb-3 opacity-65"
          style="border-color: var(--ink-soft);"
        >
          <p class="text-xs italic opacity-65 font-mono">
            ${isPt
              ? 'A voz não responde mais.'
              : 'The voice no longer answers.'}
          </p>
        </div>
      `;
    }
    if (accepted) {
      const elapsed = Date.now() - s.meta.npcAcceptedAt;
      const total = 3 * 24 * 60 * 60 * 1000;
      const remainMs = Math.max(0, total - elapsed);
      const remainH = Math.floor(remainMs / 3600000);
      const remainD = Math.floor(remainH / 24);
      const text = remainD > 0
        ? `${remainD}${isPt ? 'd' : 'd'} ${remainH % 24}${isPt ? 'h' : 'h'}`
        : `${remainH}${isPt ? 'h' : 'h'}`;
      return html`
        <div class="card mb-3" style="border-color: var(--ether);">
          <div class="font-mono text-sm">
            ${isPt ? 'Súplica aceita — pendente' : 'Entreaty accepted — pending'}
          </div>
          <div class="text-[11px] italic opacity-65 mt-1 font-mono">
            ${isPt ? 'tempo restante' : 'time remaining'}: ${text}
          </div>
        </div>
      `;
    }
    return html`
      <div class="card mb-3" style="border-color: var(--ether);">
        <p class="text-sm italic font-mono">
          "${isPt ? 'Ajude-me a sair deste laço.' : 'Help me leave this loop.'}"
        </p>
        <p class="text-[11px] italic opacity-65 mt-1 font-mono">
          ${isPt
            ? 'A voz não pertence a ninguém que você saiba nomear.'
            : 'The voice belongs to no one you can name.'}
        </p>
        <div class="mt-3 flex gap-2">
          <button
            class="btn-mini realign-btn"
            @click=${() => store.getState().acceptNpcEntreaty()}
          >
            ${isPt ? 'aceitar' : 'accept'}
          </button>
        </div>
      </div>
    `;
  }

  private renderReactorsPanel(
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    const isPt = this.langCode === 'pt-br';
    const lifetime = this.toDec(s.meta.lifetimePhi);
    return html`
      ${this.panelTitle(isPt ? 'Reatores' : 'Reactors')}
      <p class="text-xs italic opacity-65 mb-3 px-1 font-mono">
        ${isPt
          ? 'Cada um consome a sua classe. O Observador amplifica os outros.'
          : 'Each consumes its class. The Observer amplifies the others.'}
      </p>
      <div class="space-y-3">
        ${REACTORS.map((def) => {
          const st = s.reactors[def.id];
          if (!st) return nothing;
          if (st.level === 0 && lifetime.lt(def.unlockPhi)) {
            return html`
              <div class="card opacity-55">
                <div class="flex justify-between items-baseline">
                  <h3 class="title text-base">??</h3>
                  <span class="font-mono text-xs opacity-60"
                    >Φ ${fmtPhi(lifetime)}/${def.unlockPhi}</span
                  >
                </div>
                <p class="text-sm italic opacity-70 mt-1">
                  ${isPt ? 'Topologia ainda silenciosa.' : 'Topology still silent.'}
                </p>
              </div>
            `;
          }
          const cost = reactorCost(def, st.level);
          const canCoin = s.resources.coin.gte(cost.coin);
          const canClass = s.contradictions[def.class].gte(cost.classPhi);
          const atMax = st.level >= def.maxLevel;
          const effectText = this.reactorEffectText(def, st.level, isPt);
          return html`
            <div class="card" style="border-color: var(--ether);">
              <div class="flex justify-between items-baseline">
                <h3 class="title text-base flex items-center gap-2">
                  <span style="color: var(--ether)">${def.glyph}</span>
                  ${this.name(def.name)}
                </h3>
                <span class="font-mono text-xs opacity-60"
                  >${isPt ? 'nv' : 'lvl'} ${st.level}/${def.maxLevel}</span
                >
              </div>
              <p class="text-sm italic opacity-75 mt-1">
                ${this.name(def.flavor)}
              </p>
              <div class="mt-2 text-xs font-mono opacity-85">
                ${effectText}
              </div>
              <div class="mt-3 flex flex-wrap gap-2 items-center">
                ${atMax
                  ? html`<span
                      class="text-[10px] uppercase tracking-widest opacity-65"
                      >${isPt ? 'no máximo' : 'at max'}</span
                    >`
                  : html`
                      <button
                        class="btn-mini realign-btn"
                        ?disabled=${!canCoin || !canClass}
                        @click=${() => store.getState().buildReactor(def.id)}
                      >
                        ${st.level === 0
                          ? isPt
                            ? 'construir'
                            : 'build'
                          : isPt
                            ? 'upgradar'
                            : 'upgrade'}
                      </button>
                      <span
                        class="font-mono text-[11px] opacity-70"
                        style=${canCoin ? '' : 'color:#ff8e6c'}
                        >${cost.coin}${RESOURCE_ICON['coin']}</span
                      >
                      <span
                        class="font-mono text-[11px] opacity-70"
                        style=${canClass ? '' : 'color:#ff8e6c'}
                        >${cost.classPhi} Φ ${def.class.slice(0, 3)}</span
                      >
                    `}
              </div>
            </div>
          `;
        })}
      </div>
    `;
  }

  private reactorEffectText(
    def: (typeof REACTORS)[number],
    level: number,
    isPt: boolean,
  ): string {
    switch (def.effect.kind) {
      case 'phi_yield_class': {
        const pct = (level * def.effect.per * 100).toFixed(0);
        return isPt
          ? `+${pct}% Φ ${def.effect.target} (atual)`
          : `+${pct}% Φ ${def.effect.target} (now)`;
      }
      case 'craft_duration': {
        const pct = (Math.abs(level * def.effect.per) * 100).toFixed(0);
        return isPt
          ? `−${pct}% duração de ofício`
          : `−${pct}% craft duration`;
      }
      case 'market_amplitude': {
        const amt = (level * def.effect.per).toFixed(3);
        return isPt
          ? `+${amt} amplitude de mercado`
          : `+${amt} market amplitude`;
      }
      case 'reactor_multiplier': {
        const pct = (level * def.effect.per * 100).toFixed(0);
        return isPt
          ? `+${pct}% efeito de cada outro reator`
          : `+${pct}% to every other reactor's output`;
      }
    }
  }

  private encounterName(id: string): string {
    const def = ENCOUNTERS.find((e) => e.id === id);
    return def ? this.name(def.name) : id;
  }

  private renderLedger(
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    const isPt = this.langCode === 'pt-br';
    return html`
      <div
        role="dialog"
        aria-modal="true"
        class="fixed inset-0 z-40 flex items-stretch justify-center backdrop-blur-md"
        style="background: rgba(20,12,8,0.55);"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this.ledgerOpen = false;
        }}
      >
        <div
          class="m-4 md:m-10 mx-auto max-w-3xl w-full flex flex-col animate-fade-in card"
          style="background: var(--bg-2); border-color: var(--accent);"
        >
          <header class="flex items-center justify-between p-4 border-b" style="border-color: var(--line)">
            <h2 class="title text-lg flex items-center gap-3">
              <span style="color: var(--accent)">𝕷</span>
              ${isPt ? 'Livro-Razão' : 'Ledger'}
              <span class="font-mono text-xs opacity-60"
                >${s.ledger.length}/${300}</span
              >
            </h2>
            <button
              class="btn-mini"
              @click=${() => (this.ledgerOpen = false)}
            >
              ${isPt ? 'fechar' : 'close'}
            </button>
          </header>
          <div class="px-4 py-1 text-xs font-mono opacity-70 flex justify-between" style="border-bottom: 1px solid var(--line)">
            <span>${isPt ? 'tempo' : 'time'}</span>
            <span>${isPt ? 'evento' : 'event'}</span>
            <span>Φ ${isPt ? 'colhido' : 'yield'}</span>
          </div>
          ${s.act === 4 || s.act === 'endgame'
            ? html`
                <div
                  class="px-4 py-2 text-xs font-mono flex justify-between sticky top-0 z-10"
                  style="background: var(--bg-2); color: var(--ether); border-bottom: 1px solid var(--line);"
                  title=${isPt ? 'Comportamento do observador' : 'Observer behavior'}
                >
                  <span>${isPt ? 'observador' : 'observer'}</span>
                  <span>
                    ${this.observerClicksPerMin(s).toFixed(1)}
                    ${isPt ? 'cliques/min (últimos 60s)' : 'clicks/min (last 60s)'}
                  </span>
                  <span>·</span>
                </div>
              `
            : nothing}
          <div class="overflow-y-auto flex-1 max-h-[70vh] font-mono text-sm">
            ${s.ledger.length === 0
              ? html`<div class="p-6 text-center italic opacity-60">
                  ${isPt
                    ? 'O livro-razão está vazio. Por enquanto.'
                    : 'The ledger is empty. For now.'}
                </div>`
              : repeat(
                  s.ledger,
                  (e, i) => e.t + ':' + i,
                  (e: LedgerEntry) => html`
                    <div
                      class="flex items-baseline justify-between gap-3 px-4 py-1.5 border-b"
                      style="border-color: var(--line)"
                    >
                      <span class="opacity-55 shrink-0 text-xs"
                        >${formatTime(e.t)}</span
                      >
                      <span class="flex-1">${isPt ? e.pt : e.en}</span>
                      <span
                        class="opacity-70 shrink-0 tabular-nums"
                        style="color: var(--accent)"
                        >${e.phi > 0 ? '+' + e.phi.toFixed(1) : '·'}</span
                      >
                    </div>
                  `,
                )}
          </div>
          <footer class="p-3 text-xs opacity-65 flex justify-between items-center" style="border-top: 1px solid var(--line)">
            <span class="italic">
              ${isPt
                ? 'Cada linha foi escrita enquanto você não olhava.'
                : 'Each line was written while you were not watching.'}
            </span>
            <button
              class="btn-mini"
              @click=${() => {
                if (
                  confirm(
                    isPt
                      ? 'Apagar todas as linhas do livro-razão? (não afeta progresso)'
                      : 'Erase every ledger row? (does not affect progress)',
                  )
                )
                  store.getState().clearLedger();
              }}
            >
              ${isPt ? 'limpar' : 'clear'}
            </button>
          </footer>
        </div>
      </div>
    `;
  }

  private renderEncountersPanel(
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    const isPt = this.langCode === 'pt-br';
    return html`
      ${this.panelTitle(isPt ? 'Jornadas' : 'Encounters')}
      <div class="space-y-3">
        ${ENCOUNTERS.map((def) => {
          const st = s.encounters[def.id];
          if (!st.unlocked) {
            const cond: string[] = [];
            if (def.unlockXp > 0)
              cond.push(
                (isPt ? 'XP ' : 'XP ') +
                  fmtInt(s.resources.xp) +
                  '/' +
                  def.unlockXp,
              );
            if (def.unlockCoin > 0)
              cond.push(
                (isPt ? 'Moedas ' : 'Coin ') +
                  fmtInt(s.resources.coin) +
                  '/' +
                  def.unlockCoin,
              );
            return html`
              <div class="card opacity-60">
                <div class="flex justify-between items-baseline">
                  <h3 class="title text-base">??</h3>
                  <span class="font-mono text-xs opacity-60"
                    >${cond.join(' · ')}</span
                  >
                </div>
                <p class="text-sm italic opacity-70 mt-1">
                  ${isPt ? 'Ainda não revelado.' : 'Not yet revealed.'}
                </p>
              </div>
            `;
          }
          const pct = Math.min(100, (st.progress / def.duration) * 100);
          return html`
            <button
              class="card text-left w-full hover:translate-y-[-1px] transition-transform"
              @click=${() => this.engage(def.id)}
              title=${isPt ? 'Clique para avançar um passo' : 'Click to take a step'}
            >
              <div class="flex justify-between items-baseline">
                <h3 class="title text-base">${this.name(def.name)}</h3>
                <span class="font-mono text-xs opacity-60"
                  >${fmtInt(st.completions)}×</span
                >
              </div>
              <p class="text-sm italic opacity-75 mt-1">
                ${this.name(def.flavor)}
              </p>
              <div class="progress mt-3">
                <div class="progress-fill" style="width:${pct}%"></div>
              </div>
              <div class="mt-1 flex justify-between text-[11px] font-mono opacity-65">
                ${this.encounterRateText(def, s)}
                <span>+${def.xpGain} ${isPt ? 'exp' : 'xp'}</span>
              </div>
              <div
                class="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs font-mono opacity-75"
              >
                ${def.drops.map(
                  (drop) => html`
                    <span
                      >${RESOURCE_ICON[drop.id] ?? ''}
                      ${drop.min === drop.max
                        ? drop.min
                        : `${drop.min}–${drop.max}`}
                      ${this.resourceName(drop.id)}${drop.chance
                        ? ` ·${Math.round(drop.chance * 100)}%`
                        : ''}</span
                    >
                  `,
                )}
              </div>
            </button>
          `;
        })}
      </div>
    `;
  }

  private renderInventoryPanel(
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    const isPt = this.langCode === 'pt-br';
    const useMarket = s.act === 3 || s.act === 4 || s.act === 'endgame';
    const rows: ResourceId[] = [
      'wood',
      'ore',
      'hide',
      'herb',
      'ether',
      'crystal',
      'silk',
      'bone',
      'plank',
      'ingot',
      'salve',
      'charm',
      'shortSword',
      'cart',
      'reinforcedCharm',
      'elixir',
      'anvilPiece',
      'lantern',
      'tome',
      'reliquary',
      'standard',
      'swordOfFirstCut',
    ];
    return html`
      ${this.panelTitle(isPt ? 'Inventário' : 'Inventory')}
      <div class="card">
        <table class="w-full text-sm">
          <tbody>
            ${rows.map((id) => {
              const item = ITEMS[id];
              const amount = s.resources[id];
              if (amount.lte(0)) return nothing;
              const preserved = item.kind === 'quest';
              return html`
                <tr class="border-b last:border-0" style="border-color: var(--line)">
                  <td class="py-2 pr-2">
                    <span class="mr-1" style="color:var(--accent)"
                      >${RESOURCE_ICON[id] ?? '·'}</span
                    >
                    <span>${this.resourceName(id)}</span>
                    ${item.hint
                      ? html`<div class="text-[11px] italic opacity-55 mt-0.5">
                          ${this.name(item.hint)}
                        </div>`
                      : nothing}
                  </td>
                  <td class="py-2 pr-2 text-right font-mono tabular-nums">
                    ${fmtInt(amount)}
                  </td>
                  <td class="py-2 text-right">
                    ${preserved
                      ? html`<span
                          class="text-[10px] uppercase tracking-widest opacity-55"
                          >${isPt ? 'Preservado' : 'Preserved'}</span
                        >`
                      : item.sell > 0
                        ? useMarket
                          ? html`<span
                              class="text-[10px] uppercase tracking-widest opacity-55 font-mono"
                              >${isPt ? '→ mercado' : '→ market'}</span
                            >`
                          : html`
                              <div class="inline-flex items-center gap-1">
                                <button
                                  class="btn-mini"
                                  @click=${() => this.sell(id, 1)}
                                  title=${isPt ? 'Vender 1' : 'Sell 1'}
                                >
                                  ${isPt ? 'Vender' : 'Sell'} ·
                                  ${item.sell}${RESOURCE_ICON['coin']}
                                </button>
                                <button
                                  class="btn-mini"
                                  @click=${() => this.sell(id, 10)}
                                >
                                  ×10
                                </button>
                                <button
                                  class="btn-mini"
                                  @click=${() =>
                                    this.sell(id, Number.MAX_SAFE_INTEGER)}
                                >
                                  ${isPt ? 'Tudo' : 'All'}
                                </button>
                              </div>
                            `
                        : nothing}
                  </td>
                </tr>
              `;
            })}
            ${rows.every((id) => s.resources[id].lte(0))
              ? html`<tr>
                  <td class="py-6 text-center opacity-60 italic" colspan="3">
                    ${isPt
                      ? 'Nada ainda. Caminhe um pouco.'
                      : 'Nothing yet. Walk a little.'}
                  </td>
                </tr>`
              : nothing}
          </tbody>
        </table>
      </div>
    `;
  }

  private renderCraftingPanel(
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    const isPt = this.langCode === 'pt-br';
    const editorAvailable =
      s.act === 3 || s.act === 4 || s.act === 'endgame';
    return html`
      ${this.panelTitle(isPt ? 'Ofício' : 'Crafting')}
      ${editorAvailable
        ? html`
            <div class="flex justify-between items-center mb-3 px-1">
              <span class="text-[11px] uppercase tracking-widest opacity-65 font-mono">
                ${isPt ? 'Autoria' : 'Authorship'}:
                ${s.userRecipes.length}/12
              </span>
              <button
                class="btn-mini realign-btn"
                @click=${() => {
                  this.resetEditor();
                  this.editorOpen = true;
                }}
              >
                ${isPt ? 'autorar' : 'author'}
              </button>
            </div>
            ${s.userRecipes.length > 0
              ? html`<div class="space-y-3 mb-4">
                  ${s.userRecipes.map((u) => this.renderUserRecipeCard(u, s))}
                </div>`
              : nothing}
          `
        : nothing}
      <div class="space-y-3">
        ${RECIPES.map((def) => {
          const st = s.recipes[def.id];
          if (!st.unlocked) {
            return html`
              <div class="card opacity-60">
                <div class="flex justify-between items-baseline">
                  <h3 class="title text-base">??</h3>
                  <span class="font-mono text-xs opacity-60"
                    >${isPt ? 'XP ' : 'XP '} ${fmtInt(s.resources.xp)}/${def.unlockXp}</span
                  >
                </div>
                <p class="text-sm italic opacity-70 mt-1">
                  ${isPt ? 'Receita ainda oculta.' : 'Recipe still hidden.'}
                </p>
              </div>
            `;
          }
          const inputsHave = def.inputs.every((inp) =>
            s.resources[inp.id].gte(inp.amount),
          );
          const isQuestDone =
            def.preserved && s.resources[def.output.id].gte(1);
          const effDur = Math.max(0.5, def.duration * reactorDurationMultiplier(s));
          const pct =
            st.queued > 0 ? Math.min(100, (st.progress / effDur) * 100) : 0;
          return html`
            <div class="card">
              <div class="flex justify-between items-baseline">
                <h3 class="title text-base flex items-center gap-2">
                  <span style="color:var(--accent)"
                    >${RESOURCE_ICON[def.output.id] ?? ''}</span
                  >
                  ${this.name(def.name)}
                  ${def.preserved
                    ? html`<span
                        class="text-[10px] uppercase tracking-widest opacity-60"
                        >${isPt ? 'Único' : 'Unique'}</span
                      >`
                    : nothing}
                </h3>
                <span class="font-mono text-xs opacity-60"
                  >${fmtInt(st.completions)}×</span
                >
              </div>
              <div class="text-sm mt-1 opacity-80 font-mono">
                ${this.renderRecipeForm(def, s)}
                <span class="opacity-60"> · ${def.duration}s</span>
              </div>
              ${st.queued > 0
                ? html`
                    <div class="mt-3">
                      <div class="progress">
                        <div class="progress-fill" style="width:${pct}%"></div>
                      </div>
                      <div
                        class="mt-1 flex justify-between text-xs font-mono opacity-75"
                      >
                        <span
                          >${isPt ? 'Na fila' : 'Queued'}: ${st.queued}</span
                        >
                        <button
                          class="opacity-60 hover:opacity-100"
                          @click=${() => this.cancelCraft(def.id)}
                        >
                          ${isPt ? 'cancelar 1' : 'cancel 1'}
                        </button>
                      </div>
                    </div>
                  `
                : nothing}
              <div class="mt-3 flex flex-wrap gap-2">
                <button
                  class="btn-mini"
                  ?disabled=${!inputsHave || isQuestDone}
                  @click=${() => this.craft(def.id, 1)}
                >
                  ${isPt ? 'Forjar 1' : 'Forge 1'}
                </button>
                ${def.preserved
                  ? nothing
                  : html`
                      <button
                        class="btn-mini"
                        ?disabled=${!inputsHave}
                        @click=${() => this.craft(def.id, 5)}
                      >
                        ×5
                      </button>
                      <button
                        class="btn-mini"
                        ?disabled=${!inputsHave}
                        @click=${() => this.craft(def.id, 25)}
                      >
                        ×25
                      </button>
                    `}
                ${isQuestDone
                  ? html`<span class="text-xs italic opacity-70 self-center"
                      >${isPt
                        ? 'Já forjado. Não há repetição.'
                        : 'Already forged. No repetition.'}</span
                    >`
                  : nothing}
              </div>
            </div>
          `;
        })}
      </div>
    `;
  }

  private renderRecipeForm(
    def: (typeof RECIPES)[number],
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    const isAct3 = s.act === 3 || s.act === 4 || s.act === 'endgame';
    if (!isAct3) {
      return html`
        <span style="font-family: var(--font-body)">
          ${def.inputs
            .map((inp) => `${inp.amount} ${this.resourceName(inp.id)}`)
            .join(' + ')}
          →
          <span style="color: var(--accent)"
            >${def.output.amount} ${this.resourceName(def.output.id)}</span
          >
        </span>
      `;
    }
    // Boolean form
    return html`
      ${def.inputs
        .map(
          (inp) =>
            `${inp.amount > 1 ? inp.amount + '·' : ''}${this.resourceName(inp.id)}`,
        )
        .join(' ∧ ')}
      <span style="color: var(--accent)"> ⊢ </span>
      <span style="color: var(--accent)"
        >${def.output.amount > 1 ? def.output.amount + '·' : ''}${this.resourceName(
          def.output.id,
        )}</span
      >
    `;
  }

  private renderUserRecipeCard(
    u: UserRecipe,
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    const isPt = this.langCode === 'pt-br';
    const st = s.recipes[u.id];
    if (!st) return html``;
    const inputsHave = u.inputs.every((inp) =>
      s.resources[inp.id].gte(inp.amount),
    );
    const effDur = Math.max(0.5, u.duration * reactorDurationMultiplier(s));
    const pct =
      st.queued > 0 ? Math.min(100, (st.progress / effDur) * 100) : 0;
    return html`
      <div class="card" style="border-color: var(--ether);">
        <div class="flex justify-between items-baseline">
          <h3
            class="title text-base flex items-center gap-2"
            style="color: var(--ether)"
          >
            <span>${u.label}</span>
            ${u.mirror
              ? html`<span
                  class="text-[10px] uppercase tracking-widest opacity-65"
                  >${isPt ? 'espelho' : 'mirror'}</span
                >`
              : nothing}
          </h3>
          <button
            class="opacity-50 hover:opacity-100 text-xs"
            @click=${() => {
              if (
                confirm(
                  isPt
                    ? 'Apagar esta receita autoral? (reembolsa fila)'
                    : 'Delete this authored recipe? (refunds queue)',
                )
              )
                store.getState().removeUserRecipe(u.id);
            }}
            title=${isPt ? 'remover' : 'remove'}
          >
            ✕
          </button>
        </div>
        <div class="text-sm mt-1 opacity-90 font-mono">
          ${u.inputs
            .map(
              (inp) =>
                `${inp.amount > 1 ? inp.amount + '·' : ''}${this.resourceName(inp.id)}`,
            )
            .join(' ∧ ')}
          <span style="color: var(--accent)"> ⊢ </span>
          <span style="color: var(--accent)"
            >${u.output.amount > 1 ? u.output.amount + '·' : ''}${this.resourceName(
              u.output.id,
            )}</span
          >
          <span class="opacity-60"> · ${u.duration}s</span>
        </div>
        ${st.queued > 0
          ? html`
              <div class="mt-3">
                <div class="progress">
                  <div class="progress-fill" style="width:${pct}%"></div>
                </div>
                <div class="mt-1 flex justify-between text-xs font-mono opacity-75">
                  <span>${isPt ? 'Na fila' : 'Queued'}: ${st.queued}</span>
                  <button
                    class="opacity-60 hover:opacity-100"
                    @click=${() => this.cancelCraft(u.id)}
                  >
                    ${isPt ? 'cancelar 1' : 'cancel 1'}
                  </button>
                </div>
              </div>
            `
          : nothing}
        <div class="mt-3 flex flex-wrap gap-2">
          <button
            class="btn-mini"
            ?disabled=${!inputsHave}
            @click=${() => this.craft(u.id, 1)}
          >
            ${isPt ? 'Forjar 1' : 'Forge 1'}
          </button>
          <button
            class="btn-mini"
            ?disabled=${!inputsHave}
            @click=${() => this.craft(u.id, 5)}
          >
            ×5
          </button>
          <button
            class="btn-mini"
            ?disabled=${!inputsHave}
            @click=${() => this.craft(u.id, 25)}
          >
            ×25
          </button>
        </div>
      </div>
    `;
  }

  private renderRecipeEditor(
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    const isPt = this.langCode === 'pt-br';
    const primitives = this.editorInputs.length + 1;
    const phiCost = userRecipePhiCost(s, primitives);
    const inputs = this.editorInputs.filter((i) => i.amount > 0);
    const inputValue = inputs.reduce(
      (sum, i) => sum + Math.max(1, ITEMS[i.id].sell) * i.amount,
      0,
    );
    const outputValue =
      Math.max(1, ITEMS[this.editorOutput.id].sell) * this.editorOutput.amount;
    const ratio = inputValue > 0 ? outputValue / inputValue : Infinity;
    const ratioOk = ratio <= 1 / USER_RECIPE_VALUE_RATIO;
    const mirror = inputs.some((i) => i.id === this.editorOutput.id);
    const allSellable: ResourceId[] = [
      'wood',
      'ore',
      'hide',
      'herb',
      'ether',
      'crystal',
      'silk',
      'bone',
      'plank',
      'ingot',
      'salve',
      'charm',
      'shortSword',
      'cart',
      'reinforcedCharm',
      'elixir',
      'anvilPiece',
      'lantern',
      'tome',
      'reliquary',
      'standard',
    ];
    const canSubmit =
      inputs.length > 0 &&
      this.editorOutput.amount > 0 &&
      s.phi.gte(phiCost) &&
      ratioOk &&
      s.userRecipes.length < USER_RECIPE_MAX;
    return html`
      <div
        role="dialog"
        aria-modal="true"
        class="fixed inset-0 z-40 flex items-center justify-center backdrop-blur-md"
        style="background: rgba(10,8,16,0.65);"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this.editorOpen = false;
        }}
      >
        <div
          class="m-4 max-w-xl w-full animate-fade-in card"
          style="background: var(--bg-2); border-color: var(--ether);"
        >
          <header
            class="p-5 border-b"
            style="border-color: var(--line)"
          >
            <h2
              class="title text-xl flex items-center gap-3"
              style="color: var(--ether)"
            >
              ${isPt ? 'Autorar Receita' : 'Author a Recipe'}
            </h2>
            <p class="text-sm italic opacity-80 mt-2">
              ${isPt
                ? `Cada primitivo (entrada ou saída) custa ${userRecipePhiCost(s, 1)} Φ.`
                : `Each primitive (input or output) costs ${userRecipePhiCost(s, 1)} Φ.`}
            </p>
          </header>
          <div class="p-5 space-y-4 text-sm font-mono">
            <div>
              <div class="text-[11px] uppercase tracking-widest opacity-65 mb-2">
                ${isPt ? 'entradas' : 'inputs'}
              </div>
              <div class="space-y-2">
                ${this.editorInputs.map(
                  (inp, i) => html`
                    <div class="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        max="20"
                        .value=${String(inp.amount)}
                        @input=${(e: Event) =>
                          this.setEditorInput(i, {
                            amount: Math.max(
                              1,
                              parseInt(
                                (e.target as HTMLInputElement).value || '1',
                                10,
                              ) || 1,
                            ),
                          })}
                        class="editor-num"
                      />
                      <span style="color: var(--accent)">·</span>
                      <select
                        class="editor-sel"
                        .value=${inp.id}
                        @change=${(e: Event) =>
                          this.setEditorInput(i, {
                            id: (e.target as HTMLSelectElement).value as ResourceId,
                          })}
                      >
                        ${allSellable.map(
                          (r) =>
                            html`<option value=${r}>
                              ${this.resourceName(r)}
                            </option>`,
                        )}
                      </select>
                      ${this.editorInputs.length > 1
                        ? html`<button
                            class="opacity-60 hover:opacity-100 px-2"
                            @click=${() => this.removeEditorInput(i)}
                          >
                            ✕
                          </button>`
                        : nothing}
                    </div>
                  `,
                )}
              </div>
              ${this.editorInputs.length < 3
                ? html`<button
                    class="btn-mini mt-2"
                    @click=${() => this.addEditorInput()}
                  >
                    ${isPt ? '+ entrada' : '+ input'}
                  </button>`
                : nothing}
            </div>

            <div>
              <div class="text-[11px] uppercase tracking-widest opacity-65 mb-2">
                ${isPt ? 'saída' : 'output'}
              </div>
              <div class="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="20"
                  .value=${String(this.editorOutput.amount)}
                  @input=${(e: Event) =>
                    this.setEditorOutput({
                      amount: Math.max(
                        1,
                        parseInt(
                          (e.target as HTMLInputElement).value || '1',
                          10,
                        ) || 1,
                      ),
                    })}
                  class="editor-num"
                />
                <span style="color: var(--accent)">·</span>
                <select
                  class="editor-sel"
                  .value=${this.editorOutput.id}
                  @change=${(e: Event) =>
                    this.setEditorOutput({
                      id: (e.target as HTMLSelectElement).value as ResourceId,
                    })}
                >
                  ${allSellable.map(
                    (r) =>
                      html`<option value=${r}>
                        ${this.resourceName(r)}
                      </option>`,
                  )}
                </select>
              </div>
            </div>

            <div class="divider-ornate my-2">·</div>

            <div class="space-y-1 text-xs">
              <div class="flex justify-between">
                <span class="opacity-70">${isPt ? 'forma' : 'form'}</span>
                <span style="color: var(--accent)">
                  ${inputs
                    .map(
                      (i) =>
                        `${i.amount > 1 ? i.amount + '·' : ''}${this.resourceName(i.id)}`,
                    )
                    .join(' ∧ ')}
                  ⊢
                  ${this.editorOutput.amount > 1
                    ? this.editorOutput.amount + '·'
                    : ''}${this.resourceName(this.editorOutput.id)}
                </span>
              </div>
              <div class="flex justify-between">
                <span class="opacity-70">${isPt ? 'custo' : 'cost'}</span>
                <span
                  style=${s.phi.gte(phiCost) ? '' : 'color:#ff8e6c'}
                >${phiCost} Φ</span>
              </div>
              <div class="flex justify-between">
                <span class="opacity-70"
                  >${isPt ? 'razão valor' : 'value ratio'}</span
                >
                <span
                  style=${ratioOk
                    ? 'color: #6fd66f'
                    : 'color: #ff8e6c'}
                >×${ratio === Infinity ? '∞' : ratio.toFixed(2)}</span>
              </div>
              ${mirror
                ? html`<div
                    class="text-[11px] italic opacity-70"
                    style="color: var(--ether)"
                  >
                    ${isPt
                      ? '⚲ Padrão espelho detectado.'
                      : '⚲ Mirror pattern detected.'}
                  </div>`
                : nothing}
              ${this.editorError
                ? html`<div
                    class="text-[11px] italic"
                    style="color: #ff8e6c"
                  >
                    ${this.editorError}
                  </div>`
                : nothing}
            </div>
          </div>
          <footer
            class="p-4 flex justify-end gap-3"
            style="border-top: 1px solid var(--line)"
          >
            <button class="btn-mini" @click=${() => (this.editorOpen = false)}>
              ${isPt ? 'cancelar' : 'cancel'}
            </button>
            <button
              class="btn-mini realign-btn"
              ?disabled=${!canSubmit}
              @click=${() => this.submitEditor()}
            >
              ${isPt ? 'autorar' : 'author'} · ${phiCost} Φ
            </button>
          </footer>
        </div>
      </div>
    `;
  }

  private observerClicksPerMin(s: ReturnType<typeof store.getState>): number {
    const now = Date.now();
    const recent = s.observerClicks.filter((t) => now - t < 60_000);
    return recent.length;
  }

  private renderAxiomsModal(
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    const isPt = this.langCode === 'pt-br';
    return html`
      <div
        role="dialog"
        aria-modal="true"
        class="fixed inset-0 z-40 flex items-center justify-center backdrop-blur-md"
        style="background: rgba(10,8,16,0.7);"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this.axiomsOpen = false;
        }}
      >
        <div
          class="m-4 max-w-2xl w-full animate-fade-in card"
          style="background: var(--bg-2); border-color: var(--ether);"
        >
          <header class="p-5 border-b flex items-baseline justify-between" style="border-color: var(--line)">
            <div>
              <h2 class="title text-xl" style="color: var(--ether)">
                ${isPt ? 'Axiomas' : 'Axioms'}
              </h2>
              <p class="text-sm italic opacity-80 mt-2">
                ${isPt
                  ? 'Regras que mudam o mundo, não números.'
                  : 'Rules that change the world, not numbers.'}
              </p>
            </div>
            <div class="text-right">
              <div class="font-mono text-xs opacity-70">
                ${isPt ? 'pontos' : 'points'}
              </div>
              <div class="font-mono text-2xl" style="color: var(--ether)">
                ${s.meta.axiomPoints}
              </div>
            </div>
          </header>
          <div class="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
            ${AXIOMS.map((a) => {
              const owned = s.meta.axioms.includes(a.id);
              const canAfford = !owned && s.meta.axiomPoints >= a.cost;
              return html`
                <div
                  class="card flex justify-between gap-3"
                  style=${owned
                    ? 'border-color: var(--ether); background: rgba(124,92,255,0.08);'
                    : ''}
                >
                  <div class="flex-1">
                    <div class="font-title text-sm tracking-wider">
                      "${isPt ? a.rule.pt : a.rule.en}"
                    </div>
                    <div class="text-[11px] italic opacity-65 mt-1 font-mono">
                      ${isPt ? a.hint.pt : a.hint.en}
                    </div>
                  </div>
                  <div class="flex flex-col items-end gap-1">
                    <span class="font-mono text-xs opacity-70">
                      ${a.cost} ${isPt ? 'pts' : 'pts'}
                    </span>
                    ${owned
                      ? html`<span
                          class="text-[10px] uppercase tracking-widest"
                          style="color: var(--ether)"
                          >${isPt ? 'adotado' : 'adopted'}</span
                        >`
                      : html`<button
                          class="btn-mini realign-btn"
                          ?disabled=${!canAfford}
                          @click=${() => store.getState().buyAxiom(a.id)}
                        >
                          ${isPt ? 'adotar' : 'adopt'}
                        </button>`}
                  </div>
                </div>
              `;
            })}
          </div>
          <footer class="p-3 flex justify-end" style="border-top: 1px solid var(--line)">
            <button class="btn-mini" @click=${() => (this.axiomsOpen = false)}>
              ${isPt ? 'fechar' : 'close'}
            </button>
          </footer>
        </div>
      </div>
    `;
  }

  private renderRecompileModal(
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    const isPt = this.langCode === 'pt-br';
    const earned = axiomPointsEarned(this.toDec(s.meta.lifetimePhi));
    const framesKept = hasAxiom(s, 'framesInherited')
      ? Math.floor(s.meta.frames * 0.5)
      : 0;
    return html`
      <div
        role="dialog"
        aria-modal="true"
        class="fixed inset-0 z-40 flex items-center justify-center backdrop-blur-md"
        style="background: rgba(10,8,16,0.75);"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this.recompileOpen = false;
        }}
      >
        <div
          class="m-4 max-w-lg w-full animate-fade-in card"
          style="background: var(--bg-2); border-color: var(--ether);"
        >
          <header class="p-5 border-b" style="border-color: var(--line)">
            <h2 class="title text-xl" style="color: var(--ether)">
              ${isPt ? 'Recompilar' : 'Recompile'}
            </h2>
            <p class="text-sm italic opacity-80 mt-2">
              ${isPt
                ? 'Reset mais profundo. Você guarda axiomas; perde Frames.'
                : 'A deeper reset. You keep axioms; you lose Frames.'}
            </p>
          </header>
          <div class="p-5 space-y-2 text-sm font-mono">
            <div class="flex justify-between">
              <span class="opacity-70">${isPt ? 'Φ vitalício' : 'lifetime Φ'}</span>
              <span>${fmtPhi(this.toDec(s.meta.lifetimePhi))}</span>
            </div>
            <div class="flex justify-between" style="color: var(--ether)">
              <span>${isPt ? 'pontos de axioma' : 'axiom points'}</span>
              <span>+${earned}</span>
            </div>
            <div class="flex justify-between">
              <span class="opacity-70">${isPt ? 'Frames' : 'Frames'}</span>
              <span>${s.meta.frames} → ${framesKept}</span>
            </div>
            <div class="divider-ornate my-3">·</div>
            <p class="text-[11px] italic opacity-70 font-sans">
              ${isPt
                ? 'Apagado: tudo. Permanece: axiomas, marcos narrativos, contagem de Recompiles.'
                : 'Erased: everything. Kept: axioms, narrative beats, Recompile count.'}
            </p>
          </div>
          <footer class="p-4 flex justify-end gap-3" style="border-top: 1px solid var(--line)">
            <button class="btn-mini" @click=${() => (this.recompileOpen = false)}>
              ${isPt ? 'cancelar' : 'cancel'}
            </button>
            <button
              class="btn-mini realign-btn"
              ?disabled=${earned <= 0}
              @click=${() => {
                store.getState().recompile();
                this.recompileOpen = false;
                save();
              }}
            >
              ${isPt ? 'recompilar' : 'recompile'} · +${earned}
            </button>
          </footer>
        </div>
      </div>
    `;
  }

  private renderRealignModal(
    s: ReturnType<typeof store.getState>,
  ): TemplateResult {
    const isPt = this.langCode === 'pt-br';
    const earned = framesEarned(s.phi);
    const before = s.meta.frames;
    const after = before + earned;
    const mBefore = frameMultiplier(before);
    const mAfter = frameMultiplier(after);
    return html`
      <div
        role="dialog"
        aria-modal="true"
        class="fixed inset-0 z-40 flex items-center justify-center backdrop-blur-md"
        style="background: rgba(10,8,16,0.65);"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this.realignOpen = false;
        }}
      >
        <div
          class="m-4 max-w-lg w-full animate-fade-in card"
          style="background: var(--bg-2); border-color: var(--ether);"
        >
          <header class="p-5 border-b" style="border-color: var(--line)">
            <h2 class="title text-xl flex items-center gap-3" style="color: var(--ether)">
              ${isPt ? 'Realinhar' : 'Realign'}
            </h2>
            <p class="text-sm italic opacity-80 mt-2">
              ${isPt
                ? 'A Máquina oferece reverter o reino. Você guarda a forma; perde o conteúdo.'
                : 'The Engine offers to revert the kingdom. You keep the shape; you lose the contents.'}
            </p>
          </header>
          <div class="p-5 space-y-3 text-sm">
            <div class="flex justify-between font-mono">
              <span class="opacity-70">${isPt ? 'Φ disponível' : 'Φ available'}</span>
              <span>${fmtPhi(s.phi)}</span>
            </div>
            <div class="flex justify-between font-mono">
              <span class="opacity-70"
                >${isPt ? 'Frames atuais' : 'Current Frames'}</span
              >
              <span>${before} (×${mBefore.toFixed(2)})</span>
            </div>
            <div
              class="flex justify-between font-mono"
              style="color: var(--ether)"
            >
              <span>${isPt ? 'Após realinhar' : 'After realign'}</span>
              <span>${after} (×${mAfter.toFixed(2)})</span>
            </div>
            <div class="divider-ornate my-4">·</div>
            <p class="text-xs italic opacity-70">
              ${isPt
                ? 'Será apagado: recursos, jornadas, ofício, mãos, livro-razão. Permanece: Frames, marcos narrativos, conquistas.'
                : 'Erased: resources, encounters, crafts, stewards, ledger. Kept: Frames, narrative beats, achievements.'}
            </p>
          </div>
          <footer class="p-4 flex justify-end gap-3" style="border-top: 1px solid var(--line)">
            <button class="btn-mini" @click=${() => (this.realignOpen = false)}>
              ${isPt ? 'cancelar' : 'cancel'}
            </button>
            <button
              class="btn-mini realign-btn"
              ?disabled=${earned <= 0}
              @click=${() => this.doRealign()}
            >
              ${isPt ? 'realinhar' : 'realign'} · +${earned} Frames
            </button>
          </footer>
        </div>
      </div>
    `;
  }

  private renderFooter(): TemplateResult {
    const isPt = this.langCode === 'pt-br';
    return html`
      <footer class="max-w-5xl mx-auto px-4 md:px-8 pb-10 pt-2">
        <div class="divider-ornate w-full mb-4">· ${fmt(store.getState().totalCompletions, 0)} ·</div>
        <div class="flex flex-wrap items-center justify-between gap-3 text-xs">
          <div class="opacity-60 italic">${t('landing.note')}</div>
          <div class="flex items-center gap-2 flex-wrap">
            <button
              class="btn-mini"
              @click=${() => {
                this.muted = !this.muted;
                setMuted(this.muted);
              }}
              title=${isPt
                ? this.muted
                  ? 'Áudio mudo'
                  : 'Áudio ligado'
                : this.muted
                  ? 'Audio muted'
                  : 'Audio on'}
              aria-label="audio"
            >
              ${this.muted ? '🔇' : '🔊'}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              .value=${String(getVolume())}
              @input=${(e: Event) =>
                setVolume(parseFloat((e.target as HTMLInputElement).value))}
              style="width: 80px"
              aria-label="volume"
            />
            ${store.getState().act === 4 || store.getState().act === 'endgame'
              ? html`
                  <button
                    class="btn-mini"
                    @click=${() => store.getState().toggleRestoredFantasy()}
                    title=${isPt
                      ? store.getState().meta.restoredFantasy
                        ? 'A produção fica em ×0.5 enquanto restaurado.'
                        : 'Restaura visual do Ato I (produção ×0.5 enquanto ativo).'
                      : store.getState().meta.restoredFantasy
                        ? 'Production is ×0.5 while restored.'
                        : 'Restore Act I look (production ×0.5 while active).'}
                  >
                    ${store.getState().meta.restoredFantasy
                      ? isPt
                        ? 'restaurado ✓'
                        : 'restored ✓'
                      : isPt
                        ? 'restaurar fantasia'
                        : 'restore fantasy'}
                  </button>
                `
              : nothing}
            <button class="btn-mini" @click=${() => this.exportSave()}>
              ${isPt ? 'Exportar' : 'Export'}
            </button>
            <button class="btn-mini" @click=${() => this.importSave()}>
              ${isPt ? 'Importar' : 'Import'}
            </button>
            <button class="btn-mini" @click=${() => this.confirmReset()}>
              ${isPt ? 'Recomeçar' : 'Reset'}
            </button>
            <button
              class="btn-mini"
              @click=${() => (this.statsOpen = true)}
            >
              ${isPt ? 'stats' : 'stats'}
            </button>
            <button
              class="btn-mini"
              @click=${() => (this.beatsOpen = true)}
            >
              ${isPt ? 'marcos' : 'beats'}
            </button>
            <button
              class="btn-mini"
              @click=${() => (this.creditsOpen = true)}
              aria-label=${isPt ? 'Créditos' : 'Credits'}
            >
              ${isPt ? 'créditos' : 'credits'}
            </button>
            <div class="lang-toggle ml-2">
              <button
                aria-pressed=${this.langCode === 'en'}
                @click=${() => setLang('en')}
              >
                EN
              </button>
              <span aria-hidden="true" class="opacity-30">·</span>
              <button
                aria-pressed=${this.langCode === 'pt-br'}
                @click=${() => setLang('pt-br')}
              >
                PT-BR
              </button>
            </div>
          </div>
        </div>
      </footer>
    `;
  }

  private panelTitle(s: string): TemplateResult {
    return html`<div class="divider-ornate w-full mb-3">· ${s} ·</div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tpe-game': TpeGame;
  }
}
